import { PAGE_SX } from '../components/pageLayout';
import Header from '../components/Header';
import FlexBetween from '../components/FlexBetween';
import OnlineCountPills from '../components/OnlineCountPills';
import useGridStyles from '../hooks/useGridStyles';
import useDialogStyles from '../hooks/useDialogStyles';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
// Material UI Components
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    GlobalStyles,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Popover,
    Stack,
    Tab,
    Tabs,
    TextField,
    Switch,
    Tooltip,
    Typography,
    useMediaQuery,
    LinearProgress,
} from '@mui/material'

// Material UI Hooks
import { useTheme } from '@mui/material'

// Material UI Utilities
import { alpha } from '@mui/material'

// Material UI Icons
import {
    AddReaction, 
    AttachFile,
    Call,
    Check,
    CheckCircle,
    CheckCircleOutlined,
    Close,
    Delete,
    DeleteOutline,
    Edit,
    ExitToApp,
    Forward as ForwardIcon,
    GroupAdd,
    Groups,
    InfoOutlined,
    KeyboardArrowDown,
    KeyboardArrowUp,
    MarkChatUnread,
    MoreHoriz,
    MoreVert,
    Notifications,
    NotificationsOff,
    PersonAdd,
    Redo,
    Reply,
    Search,
    Send,
    Videocam,
    PushPin,
    PushPinOutlined,
    ArrowBack,
    GraphicEq, SmartToy,
    UnfoldLess, UnfoldMore, DriveFolderUpload, ChatBubbleOutline,
} from '@mui/icons-material'
import copy from 'copy-to-clipboard'

import { useAuthContext } from '../context/auth'
import { useUserContext } from '../context/user'
import { usePathContext } from '../context/path'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import { useMessageContext } from '../context/message'
import UserAvatar from '../components/chat/UserAvatar';
import ChatEmojiPicker from '../components/chat/ChatEmojiPicker';
import MessageContent from "../components/chat/MessageContent";
import { isOnlyEmojis } from '../utils/utils';
import socket from '../utils/socket';
import { setActiveChatUserId, consumePendingChat } from '../utils/chatState';
import { useCall } from '../context/call'
import RecordingsDialog from '../components/chat/RecordingsDialog';
import ChatAttachments from '../components/chat/ChatAttachments';
import PhotoViewer from '../components/chat/PhotoViewer';
import { getBrowserViewType, isNewAttachment } from '../utils/openableFile';
import { announceAttachmentOpened, subscribeAttachmentOpened } from '../utils/attachmentEvents';
import { ConfirmDialog } from '../components/dialogKit';
import SendFilesDialog from '../components/chat/SendFilesDialog';
import { ROLES } from '../config/roles';
import { avatarColor, avatarSrc, getInitials } from '../utils/avatar';
import EmojiPicker, { Theme as EmojiTheme } from 'emoji-picker-react';

const MAX_FILES = 10;
const fileKey = (f) => `${f.name}-${f.size}-${f.lastModified}`
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏']
const PAGE_SIZE = 40
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// "@Full Name" not followed by another letter, so "@Ann" doesn't match "@Anna"
const mentionRegex = (name) =>
  new RegExp(`@${escapeRegExp(name)}(?![\\p{L}\\p{N}_])`, 'giu')

// Add or remove my reaction in a reactions list (used for the instant, optimistic update)
const toggleLocalReaction = (reactions, emoji, meId) => {
  const list = (reactions || []).map((r) => ({ emoji: r.emoji, userIds: [...r.userIds] }))
  const entry = list.find((r) => r.emoji === emoji)

  if (!entry) return [...list, { emoji, userIds: [meId] }]

  if (entry.userIds.includes(meId)) {
    entry.userIds = entry.userIds.filter((u) => u !== meId)
    return list.filter((r) => r.userIds.length > 0)
  }

  entry.userIds.push(meId)
  return list
}

const removeTyping = (prev, key, userId) => {
  if (!prev[key]?.[userId]) return prev
  const next = { ...prev, [key]: { ...prev[key] } }
  delete next[key][userId]
  if (Object.keys(next[key]).length === 0) delete next[key]
  return next
}

const TypingDots = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      gap: '2px',
      ml: 0.5,
      '& span': {
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
        animation: 'typingBlink 1.2s infinite both',
      },
      '& span:nth-of-type(2)': { animationDelay: '0.2s' },
      '& span:nth-of-type(3)': { animationDelay: '0.4s' },
      '@keyframes typingBlink': {
        '0%, 80%, 100%': { opacity: 0.2 },
        '40%': { opacity: 1 },
      },
    }}
  >
    <span /><span /><span />
  </Box>
)

const toGroupList = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.groups) ? data.groups : []

// Chat list time, Teams style: "14:05" today, "Mon" this week, "3/12" before that
const formatChatTime = (ms) => {
  if (!ms) return ''
  const d = new Date(ms)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  if (now - d < 6 * 24 * 60 * 60 * 1000) return d.toLocaleDateString([], { weekday: 'short' })
  return d.toLocaleDateString([], { month: 'numeric', day: 'numeric' })
}

// One attached file before sending: image thumbnail, or a name pill for other files.
// progress: undefined = not uploading, 0-100 = uploading
const SelectedFileChip = ({ file, onRemove, disabled, progress }) => {
  const theme = useTheme()
  const [url, setUrl] = useState(null)
  const uploading = progress !== undefined

  useEffect(() => {
    if (!file.type?.startsWith('image/')) return
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  if (url) {
    return (
      <Box
        title={file.name}
        sx={{
          position: 'relative',
          width: 88,
          height: 88,
          flexShrink: 0,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          component="img"
          src={url}
          alt={file.name}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />

        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.45)',
            }}
          >
            {progress >= 100 ? (
              <Check sx={{ color: '#ffffff' }} />
            ) : (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="determinate" value={progress} size={42} sx={{ color: '#ffffff' }} />
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 700, fontSize: '0.65rem' }}>
                    {progress}%
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <IconButton
          size="small"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${file.name}`}
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            p: 0.25,
            color: '#ffffff',
            backgroundColor: 'rgba(0,0,0,0.6)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
          }}
        >
          <Close sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        pl: 1,
        pr: 0.5,
        py: 0.5,
        borderRadius: '999px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
        maxWidth: 260,
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        {uploading && progress >= 100 ? <Check sx={{ fontSize: '1rem' }} /> : <AttachFile sx={{ fontSize: '1rem' }} />}
      </Box>

      <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0 }}>
        {file.name}
      </Typography>

      {uploading && (
        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {progress}%
        </Typography>
      )}

      <IconButton size="small" onClick={onRemove} disabled={disabled} aria-label={`Remove ${file.name}`}>
        <Close fontSize="small" />
      </IconButton>

      {uploading && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3 }}
        />
      )}
    </Box>
  )
}

const Chat = () => {
  const theme = useTheme()
  // Same card outline and scrollbar as the DataGrid pages and dialogs
  const { gridBorder } = useGridStyles()
  const { scrollbarSx } = useDialogStyles()
  const { setTitle } = usePathContext()
  const { auth } = useAuthContext()
  const { messages, dispatchMessages } = useMessageContext()
  const { users, dispatch, setTargetUser } = useUserContext()
  const axiosPrivate = useAxiosPrivate()
  const [error, setError] = useState(null);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  // On mobile: false = contact list, true = conversation
  const [showChatOnMobile, setShowChatOnMobile] = useState(false)

  const [query, setQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [draft, setDraft] = useState('')

  const [tappedMessageId, setTappedMessageId] = useState(null)

  const [replyingTo, setReplyingTo] = useState(null);       // message being replied to
  const [highlightedId, setHighlightedId] = useState(null); // flashes the quoted original

  const [forwardMessage, setForwardMessage] = useState(null); // message being forwarded
  const [sendFilesOpen, setSendFilesOpen] = useState(false);  // "Send files" dialog
  const [forwardTargets, setForwardTargets] = useState([]);   // selected user ids
  const [forwardQuery, setForwardQuery] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [forwarding, setForwarding] = useState(false);
  const {
    startCall,
    startGroupCall,
    joinGroupCall,
    hangUp,
    inCall,
    call,
    activeGroupCalls,
    refreshGroupCall,
  } = useCall()

  const messagesRef = useRef([])
  messagesRef.current = messages

  // "messagesRead" can arrive before my own POST /api/messages returns (the other side reads
  // it instantly). Keep those signals so the message doesn't stay on "Sent" forever.
  // { [messageId]: [readerIds] } for messages not in my list yet
  const earlyReadsRef = useRef({})

  // Typing indicator: { [chatKey]: { [userId]: { name, until } } }
  const [typingMap, setTypingMap] = useState({})
  const typingTargetRef = useRef(null)
  const lastTypingEmitRef = useRef(0)

  // Reaction picker
  const [reactionAnchor, setReactionAnchor] = useState(null)
  const [reactionMessageId, setReactionMessageId] = useState(null)

  // ---------- Groups ----------
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupQuery, setGroupQuery] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  // All contacts (excluding me), latest conversation first
  const sortedUsers = useMemo(() => {
    if (!users) return [];

    return users
      .filter((user) => user._id !== auth?._id)
      .sort((a, b) => {
        if (a.isBot !== b.isBot) return a.isBot ? -1 : 1;   // bot always first
        const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;

        // Most recent message first
        if (aTime !== bTime) return bTime - aTime;

        // If neither has messages, online users first
        if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;

        // Finally, alphabetical
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [users, auth?._id]);

  // Search box only filters; it never changes the order
  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedUsers;
    return sortedUsers.filter((user) => user.name?.toLowerCase().includes(q));
  }, [sortedUsers, query]);

  const botUser = useMemo(() => filteredUsers.find((u) => u.isBot) || null, [filteredUsers]);
  const contactUsers = useMemo(() => filteredUsers.filter((u) => !u.isBot), [filteredUsers]);

  const selectedUser = Array.isArray(users)
    ? users.find((user) => user._id === selectedUserId)
    : null;

  const selectedGroup = Array.isArray(groups)
    ? groups.find((g) => g._id === selectedGroupId)
    : null;

  const isBotChat = Boolean(selectedUser?.isBot);

  // ---------- Guest: page header can be folded away for more chat room (remembered) ----------
  const [headerHidden, setHeaderHidden] = useState(() => {
    try { return localStorage.getItem('chat.headerHidden') === '1'; } catch { return false; }
  });
  const toggleHeader = () => {
    setHeaderHidden((h) => {
      try { localStorage.setItem('chat.headerHidden', h ? '0' : '1'); } catch { /* storage unavailable */ }
      return !h;
    });
  };

  // ---------- AI assistant on / off (Root switches it, everyone follows live) ----------
  const isRoot = [].concat(auth?.roles ?? []).includes(ROLES.Root);
  const [botEnabled, setBotEnabled] = useState(true);
  const [botSaving, setBotSaving] = useState(false);

  useEffect(() => {
    if (!auth?.accessToken) return;
    let alive = true;
    axiosPrivate.get('/api/messages/bot-status')
      .then(({ data }) => { if (alive) setBotEnabled(data?.enabled !== false); })
      .catch(() => {});
    const onChanged = ({ enabled }) => setBotEnabled(Boolean(enabled));
    socket.on('chatBotChanged', onChanged);
    return () => {
      alive = false;
      socket.off('chatBotChanged', onChanged);
    };
  }, [auth?.accessToken, axiosPrivate]);

  // Turned off while its chat is open: close it (the contact list no longer has it)
  useEffect(() => {
    if (botEnabled || !isBotChat) return;
    setSelectedUserId('');
    setShowChatOnMobile(false);
  }, [botEnabled, isBotChat]);

  const toggleChatBot = async () => {
    const next = !botEnabled;
    setBotEnabled(next);          // optimistic; the server broadcast confirms it
    setBotSaving(true);
    try {
      await axiosPrivate.put('/api/messages/bot-status', { enabled: next });
    } catch (err) {
      console.error('Chat: unable to switch the AI assistant', err);
      setBotEnabled(!next);
    } finally {
      setBotSaving(false);
    }
  };

  const convId = selectedGroupId || selectedUserId;          // used by settings
  const hasConversation = Boolean(selectedUser || selectedGroup);

  const userById = useMemo(
    () => Object.fromEntries((users || []).map((u) => [u._id, u])),
    [users]
  );

  // Who to show as the sender of a (non-mine) message
  const senderOf = (m) => (selectedGroup ? userById[m.senderId] : selectedUser);

  const selectedMessages = useMemo(() => {
    if (!messages?.length || !convId) return [];

    return messages
      .filter((msg) =>
        selectedGroupId
          ? String(msg.groupId) === selectedGroupId
          : !msg.groupId &&                                   // never mix group messages into 1:1
            (msg.recipientId === selectedUserId ||
            msg.senderId === selectedUserId ||
            msg.sender === selectedUserId)
      )
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [messages, selectedUserId, selectedGroupId, convId]);
  
  // ---------- Find in chat ----------
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [findIndex, setFindIndex] = useState(0);
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);

  // Teams-style tabs over the conversation: 'chat' | 'files' | 'photos'
  const [chatTab, setChatTab] = useState('chat');
  useEffect(() => { setChatTab('chat'); }, [convId]);

  // New (unopened) documents of this conversation, for the badges on the Files / Photos tabs
  const [newDocs, setNewDocs] = useState({ files: 0, photos: 0 });
  useEffect(() => {
    if (!convId) {
      setNewDocs({ files: 0, photos: 0 });
      return;
    }

    let alive = true;
    let timer = null;
    const params = selectedGroupId ? { groupId: selectedGroupId } : { recipientId: selectedUserId };

    const load = () =>
      axiosPrivate
        .get('/api/messages/attachments/unread-count', { params })
        .then(({ data }) => alive && setNewDocs({ files: data?.files || 0, photos: data?.photos || 0 }))
        .catch(() => {});

    // several events can arrive together: reload once, shortly after the last one
    const schedule = () => { clearTimeout(timer); timer = setTimeout(load, 400); };

    load();
    socket.on('newMessage', schedule);
    const unsubscribe = subscribeAttachmentOpened(schedule);

    return () => {
      alive = false;
      clearTimeout(timer);
      socket.off('newMessage', schedule);
      unsubscribe();
    };
  }, [convId, selectedGroupId, selectedUserId, axiosPrivate]);

  const [findMatches, setFindMatches] = useState([]);   // message ids, oldest -> newest

  const findMatchSet = useMemo(() => new Set(findMatches), [findMatches]);
  const currentMatchId = findMatches[findIndex] ?? null;

  const goToMatch = async (index, matches = findMatches) => {
    if (!matches.length) return;
    const next = (index + matches.length) % matches.length;
    setFindIndex(next);

    const id = matches[next];
    stickToBottomRef.current = false;   // stop auto-scroll from pulling us back down

    if (messagesRef.current.some((m) => m._id === id)) {
      document.getElementById(`msg-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Not loaded yet: page back until it is (the layout effect scrolls to it after render)
      await loadOlderPages({ untilId: id, limit: 100 });
    }
  };

  // Search on the server (debounced), then jump to the newest match like Teams
  useEffect(() => {
    const q = findQuery.trim();

    if (!findOpen || !q || !convId) {
      setFindMatches([]);
      setFindIndex(0);
      return;
    }

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosPrivate.get('/api/messages/search', {
          params: { ...convParamsRef.current, q },
          signal: controller.signal,
        });

        setFindMatches(data.ids);
        if (data.ids.length) goToMatch(data.ids.length - 1, data.ids);
        else setFindIndex(0);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Search failed', err);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findQuery, findOpen, convId]);

  const closeFind = () => {
    setFindOpen(false);
    setFindQuery('');
    setFindIndex(0);
  };

  // Highlight the matched words inside the bubbles (browsers with the CSS Highlight API)
  useEffect(() => {
    if (typeof CSS === 'undefined' || !CSS.highlights || typeof Highlight === 'undefined') return;

    const clear = () => {
      CSS.highlights.delete('find-match');
      CSS.highlights.delete('find-current');
    };

    clear();

    const q = findQuery.trim().toLowerCase();
    if (!findOpen || !q || findMatches.length === 0) return;

    const others = [];
    const current = [];

    findMatches.forEach((id) => {
      // Only search inside the bubble itself, not the timestamp or sender label
      const root = document.getElementById(`msg-${id}`)?.querySelector('.bubble-wrapper');
      if (!root) return;

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;

      while ((node = walker.nextNode())) {
        const text = node.textContent.toLowerCase();
        let idx = text.indexOf(q);

        while (idx !== -1) {
          const range = new Range();
          range.setStart(node, idx);
          range.setEnd(node, idx + q.length);
          (id === currentMatchId ? current : others).push(range);
          idx = text.indexOf(q, idx + q.length);
        }
      }
    });

    if (others.length) CSS.highlights.set('find-match', new window.Highlight(...others));
    if (current.length) CSS.highlights.set('find-current', new window.Highlight(...current));

    return clear;
  }, [findOpen, findQuery, findMatches, currentMatchId, selectedMessages]);

  // Highlight @Name inside message bubbles (mentions of me use a stronger color)
  useEffect(() => {
    if (typeof CSS === 'undefined' || !CSS.highlights || typeof Highlight === 'undefined') return;

    const clear = () => {
      CSS.highlights.delete('mention');
      CSS.highlights.delete('mention-me');
    };
    clear();

    const meId = String(auth?._id);
    const others = [];
    const mine = [];

    selectedMessages.forEach((m) => {
      if (!m.mentions?.length || m.deletedAt) return;

      const root = document.getElementById(`msg-${m._id}`)?.querySelector('.bubble-wrapper');
      if (!root) return;

      m.mentions.forEach((uid) => {
        const name = userById[String(uid)]?.name || (String(uid) === meId ? auth?.name : null);
        if (!name) return;

        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let node;

        while ((node = walker.nextNode())) {
          for (const match of node.textContent.matchAll(mentionRegex(name))) {
            const range = new Range();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            (String(uid) === meId ? mine : others).push(range);
          }
        }
      });
    });

    if (others.length) CSS.highlights.set('mention', new window.Highlight(...others));
    if (mine.length) CSS.highlights.set('mention-me', new window.Highlight(...mine));

    return clear;
  }, [selectedMessages, userById, auth?._id, auth?.name]);

  // ---------- Per-chat settings (mute / unread / delete) ----------
  const [chatSettings, setChatSettings] = useState({});   // { [peerId]: { muted, markedUnread } }
  // Live "last message" time per chat, so the list re-orders before the next users/groups refresh
  const [activityAt, setActivityAt] = useState({});       // { [userId | groupId]: ms }
  const bumpActivity = useCallback((id, at) => {
    if (!id) return
    const ms = at ? new Date(at).getTime() : Date.now()
    setActivityAt((prev) => (prev[id] >= ms ? prev : { ...prev, [id]: ms }))
  }, [])
  const [deleteChatOpen, setDeleteChatOpen] = useState(false);
  const [deletingChat, setDeletingChat] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const chatSettingsRef = useRef({});
  chatSettingsRef.current = chatSettings;

  // Let the sidebar show the same unread total
  useEffect(() => {
    if (!settingsLoaded) return;
    window.dispatchEvent(new CustomEvent('chat:unread-changed', { detail: chatSettings }));
  }, [chatSettings, settingsLoaded]);

  const isMuted = Boolean(chatSettings[convId]?.muted);

  // ---------- Shared dialog styling (gold accent on navy) ----------
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;
  const accentText = theme.palette.getContrastText(accent);
  // My chat bubbles: white text on a deep gold (the same #997d3d in both modes), dark enough for white to read
  const myBubbleBg = isDark ? theme.palette.secondary[700] : theme.palette.secondary.light;
  const myBubbleText = '#ffffff';

  const dialogPaperSx = {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    borderRadius: 2,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
    boxShadow: 8,
    overflow: 'hidden',
  };

  const dialogFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
      '& fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.28)' : theme.palette.neutral[300],
      },
      '&:hover fieldset': {
        borderColor: isDark ? 'rgba(255,255,255,0.5)' : theme.palette.neutral[400],
      },
      '&.Mui-focused fieldset': {
        borderColor: accent,
        borderWidth: 1,
      },
    },
    '& .MuiInputBase-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.8,
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: accent,
    },
  };

  const dialogListSx = {
    maxHeight: 240,
    overflow: 'auto',
    my: 1,
    py: 0.5,
    borderRadius: 1.5,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.divider}`,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : theme.palette.background.default,
    ...scrollbarSx,
  };

  const goldButtonSx = {
    backgroundColor: accent,
    color: accentText,
    fontWeight: 700,
    borderRadius: 1.5,
    px: 2.5,
    boxShadow: 'none',
    '&:hover': { backgroundColor: theme.palette.secondary.light, boxShadow: 'none' },
    '&.Mui-disabled': {
      backgroundColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
      color: theme.palette.text.disabled,
    },
  };


  const renderUnread = (id) => {
    const s = chatSettings[id];
    if (!s?.markedUnread) return null;

    // Soft "breathing" pulse: the badge dims a little while a glow ring spreads and fades
    const pulse = (color) => ({
      '--unread-glow': alpha(color, 0.55),
      animation: 'unreadPulse 2.2s ease-in-out infinite',
      '@keyframes unreadPulse': {
        '0%': { opacity: 1, boxShadow: '0 0 0 0 var(--unread-glow)' },
        '50%': { opacity: 0.72 },
        '70%': { boxShadow: '0 0 0 6px transparent' },
        '100%': { opacity: 1, boxShadow: '0 0 0 0 transparent' },
      },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    });

    if ((s.mentionCount || 0) > 0) {
      return (
        <Tooltip title="You were mentioned">
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.75,
              ml: 1,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: '0.75rem',
              fontWeight: 800,
              backgroundColor: theme.palette.error.main,
              color: theme.palette.error.contrastText,
              ...pulse(theme.palette.error.main),
            }}
          >
            @
          </Box>
        </Tooltip>
      );
    }

    const count = s.unreadCount || 0;

    if (count > 0) {
      return (
        <Box
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.75,
            ml: 1,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '0.7rem',
            fontWeight: 700,
            backgroundColor: accent,
            color: accentText,
            ...pulse(accent),
          }}
        >
          {count > 99 ? '99+' : count}
        </Box>
      );
    }

    // Marked as unread manually: no count, just a dot
    return (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          flexShrink: 0,
          ml: 1,
          bgcolor: accent,
          ...pulse(accent),
        }}
      />
    );
  };

  const [leaveGroupOpen, setLeaveGroupOpen] = useState(false);
  const [leavingGroup, setLeavingGroup] = useState(false);

  const [recordingsOpen, setRecordingsOpen] = useState(false);

  const handleLeaveGroup = async () => {
    const leftId = selectedGroupId;
    if (!leftId) return;

    setLeavingGroup(true);
    try {
      await axiosPrivate.post(`/api/groups/${leftId}/leave`);

      if (call?.kind === 'group' && call.groupId === leftId) hangUp('ended') 

      // Drop the group's messages, the group itself, and its settings from local state
      dispatchMessages({
        type: 'SET_MESSAGE',
        payload: messages.filter((m) => String(m.groupId) !== leftId),
      });
      setGroups((prev) => prev.filter((g) => g._id !== leftId));
      setChatSettings((prev) => {
        const next = { ...prev };
        delete next[leftId];
        return next;
      });

      closeFind();
      setLeaveGroupOpen(false);
      setSelectedGroupId('');   // the auto-select effect then opens your latest contact
      setShowChatOnMobile(false)
    } catch (err) {
      setLeaveGroupOpen(false);
      setError(err.response?.data?.error || 'Unable to leave group');
    } finally {
      setLeavingGroup(false);
    }
  };

  // ---------- Group members ----------
  const [membersOpen, setMembersOpen] = useState(false);
  const [membersBusy, setMembersBusy] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [renameDraft, setRenameDraft] = useState('');
  const [addMode, setAddMode] = useState(false);
  const [addTargets, setAddTargets] = useState([]);
  const [addQuery, setAddQuery] = useState('');
  const [removingId, setRemovingId] = useState(null);

  const iAmOwner =
    Boolean(selectedGroup) && String(selectedGroup.createdBy) === String(auth?._id);

  const groupMemberUsers = useMemo(() => {
    if (!selectedGroup) return [];

    return selectedGroup.members
      .map((id) =>
        userById[id] ||
        (id === auth?._id
          ? { _id: id, name: auth?.name || 'You' }
          : { _id: id, name: 'Unknown user' })
      )
      .sort((a, b) => {
        const aOwner = String(selectedGroup.createdBy) === String(a._id);
        const bOwner = String(selectedGroup.createdBy) === String(b._id);
        if (aOwner !== bOwner) return aOwner ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '');
      });
  }, [selectedGroup, userById, auth?._id, auth?.name]);

  const addCandidates = useMemo(() => {
    if (!selectedGroup) return [];
    const inGroup = new Set(selectedGroup.members);
    const q = addQuery.toLowerCase();

    return (users || []).filter(
      (u) => u._id !== auth?._id && !inGroup.has(u._id) && u.name?.toLowerCase().includes(q)
    );
  }, [users, selectedGroup, auth?._id, addQuery]);

  const openMembers = () => {
    if (!selectedGroup) return;
    setRenameDraft(selectedGroup.name);
    setAddMode(false);
    setAddTargets([]);
    setAddQuery('');
    setMembersError('');
    setMembersOpen(true);
  };

  const applyGroupUpdate = (updated) =>
    setGroups((prev) => prev.map((g) => (g._id === updated._id ? updated : g)));

  const handleRenameGroup = async () => {
    const name = renameDraft.trim();
    if (!selectedGroup || !name || name === selectedGroup.name) return;

    setMembersBusy(true);
    setMembersError('');
    try {
      const { data } = await axiosPrivate.patch(`/api/groups/${selectedGroupId}`, { name });
      applyGroupUpdate(data);
    } catch (err) {
      setMembersError(err.response?.data?.error || 'Unable to rename group');
    } finally {
      setMembersBusy(false);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedGroup || addTargets.length === 0) return;

    setMembersBusy(true);
    setMembersError('');
    try {
      const { data } = await axiosPrivate.post(`/api/groups/${selectedGroupId}/members`, {
        memberIds: addTargets,
      });
      applyGroupUpdate(data);
      setAddMode(false);
      setAddTargets([]);
      setAddQuery('');
    } catch (err) {
      setMembersError(err.response?.data?.error || 'Unable to add members');
    } finally {
      setMembersBusy(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    setRemovingId(userId);
    setMembersError('');
    try {
      const { data } = await axiosPrivate.delete(`/api/groups/${selectedGroupId}/members/${userId}`);
      applyGroupUpdate(data);
    } catch (err) {
      setMembersError(err.response?.data?.error || 'Unable to remove member');
    } finally {
      setRemovingId(null);
    }
  };
  
  // Load settings once
  useEffect(() => {
    if (!auth?.accessToken) return;
    const controller = new AbortController();

    (async () => {
      try {
        const { data } = await axiosPrivate.get('/api/messages/settings', { signal: controller.signal });
        setChatSettings(Object.fromEntries(data.map((s) => [String(s.peerId), s])));
        
        setSettingsLoaded(true);            
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Unable to load chat settings', err);
        }
      }
    })();

    return () => controller.abort();
  }, [auth?.accessToken, axiosPrivate]);

  // Optimistic update with rollback
  const updateChatSetting = async (peerId, patch) => {
    const previous = chatSettings[peerId];
    const optimistic = patch.markedUnread === false ? { ...patch, unreadCount: 0, mentionCount: 0 } : patch;

    setChatSettings((prev) => ({ ...prev, [peerId]: { ...prev[peerId], ...optimistic } }));

    try {
      await axiosPrivate.patch(`/api/messages/settings/${peerId}`, patch);
    } catch (err) {
      setChatSettings((prev) => ({ ...prev, [peerId]: previous }));
      setError(err.response?.data?.error || 'Unable to update chat settings');
    }
  };

  const handleMarkUnread = () => {
    setChatMenuAnchor(null);
    updateChatSetting(convId, { markedUnread: true });
  };

  const handleToggleMute = () => {
    setChatMenuAnchor(null);
    updateChatSetting(convId, { muted: !isMuted });
  };

  const handleDeleteChat = async () => {
    setDeletingChat(true);
    try {
      await axiosPrivate.post(`/api/messages/clear/${convId}`);

      // Drop this conversation's messages from the global list
      const ids = new Set(selectedMessages.map((m) => m._id));
      dispatchMessages({
        type: 'SET_MESSAGE',
        payload: messages.filter((m) => !ids.has(m._id)),
      });

      setChatSettings((prev) => ({
        ...prev,
        [convId]: { ...prev[convId], markedUnread: false },
      }));
      closeFind();
      setDeleteChatOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to delete chat');
    } finally {
      setDeletingChat(false);
    }
  };

  const messagesContainerRef = useRef(null);
  const chatInputRef = useRef(null);
  const contentRef = useRef(null);

  const stickToBottomRef = useRef(true);

  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileProgress, setFileProgress] = useState({});   // fileKey -> 0..100 while uploading
  const uploadAbortRef = useRef(null);

  const fileInputRef = useRef(null);

  const [hasMore, setHasMore] = useState(false);
  // ---------- @mentions ----------
  const [mentionState, setMentionState] = useState(null)   // { query, start } while the picker is open
  const [mentionIndex, setMentionIndex] = useState(0)

  const mentionCandidates = useMemo(() => {
    if (!mentionState || !selectedGroup) return []
    const q = mentionState.query.toLowerCase()

    return selectedGroup.members
      .map(String)
      .filter((id) => id !== String(auth?._id) && userById[id])
      .map((id) => userById[id])
      .filter((u) => u.name?.toLowerCase().includes(q))
      .sort((a, b) => {
        const aStarts = a.name.toLowerCase().startsWith(q)
        const bStarts = b.name.toLowerCase().startsWith(q)
        if (aStarts !== bStarts) return aStarts ? -1 : 1
        return a.name.localeCompare(b.name)
      })
      .slice(0, 8)
  }, [mentionState, selectedGroup, userById, auth?._id])

  // Open the picker when the caret sits right after "@something"
  const updateMentionState = (value, caret) => {
    if (!selectedGroup) {
      setMentionState(null)
      return
    }

    const match = /(^|\s)@([^\s@]*)$/.exec(value.slice(0, caret))
    if (!match) {
      setMentionState(null)
      return
    }

    setMentionState({ query: match[2], start: caret - match[2].length - 1 })
    setMentionIndex(0)
  }

  const insertMention = (user) => {
    if (!mentionState) return

    const input = chatInputRef.current
    const caret = input?.selectionStart ?? draft.length
    const insert = `@${user.name} `
    const next = draft.slice(0, mentionState.start) + insert + draft.slice(caret)
    const pos = mentionState.start + insert.length

    setDraft(next)
    setMentionState(null)

    setTimeout(() => {
      input?.focus()
      input?.setSelectionRange(pos, pos)
    }, 0)
  }

  // Members whose "@Name" appears in the text being sent
  const getMentionIds = (text) => {
    if (!selectedGroup) return []

    return selectedGroup.members
      .map(String)
      .filter((id) => id !== String(auth?._id) && userById[id]?.name && mentionRegex(userById[id].name).test(text))
  }

  // ---------- Pinned messages ----------
  const [pins, setPins] = useState([]);
  const [pinIndex, setPinIndex] = useState(0);
  const hasToken = Boolean(auth?.accessToken);

  const loadPins = async () => {
    const key = convIdRef.current;
    if (!key) return;

    try {
      const { data } = await axiosPrivate.get('/api/messages/pinned', { params: convParamsRef.current });
      if (convIdRef.current === key) setPins(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Unable to load pinned messages', err);
    }
  };

  const loadPinsRef = useRef(loadPins);
  loadPinsRef.current = loadPins;

  // Pins that were deleted since are hidden
  const livePins = useMemo(() => {
    const byId = new Map(messages.map((m) => [m._id, m]));
    return pins.filter((p) => !byId.get(p._id)?.deletedAt);
  }, [pins, messages]);

  const pinnedIdSet = useMemo(() => new Set(livePins.map((p) => p._id)), [livePins]);
  const pinIdx = Math.min(pinIndex, Math.max(livePins.length - 1, 0));
  const currentPin = livePins[pinIdx] || null;
  const pinSource = currentPin ? (messages.find((m) => m._id === currentPin._id) || currentPin) : null;
  const hasPin = Boolean(currentPin);

  const handleTogglePin = async (message) => {
    if (!message?._id) return;

    try {
      await axiosPrivate.put(`/api/messages/${message._id}/pin`, { pinned: !pinnedIdSet.has(message._id) });
      loadPins();
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to pin message');
    }
  };

  // Load the pins when a chat opens
  useEffect(() => {
    setPins([]);
    setPinIndex(0);
    if (!convId || !hasToken) return;
    loadPinsRef.current();
  }, [convId, hasToken]);

  // Someone pinned / unpinned in one of my chats
  useEffect(() => {
    if (!auth?._id) return;

    const onPin = ({ groupId, senderId, recipientId }) => {
      const key = convIdRef.current;
      if (!key) return;

      const concerns = groupId ? groupId === key : (senderId === key || recipientId === key);
      if (concerns) loadPinsRef.current();
    };

    socket.on('pin', onPin);
    return () => socket.off('pin', onPin);
  }, [auth?._id]);

  // The bar appearing or disappearing resizes the message area: stay at the bottom
  useEffect(() => {
    if (stickToBottomRef.current) scrollToBottom('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPin]);

  // ---------- Global search (all chats) ----------
  const [globalResults, setGlobalResults] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const pendingJumpMsgRef = useRef(null);   // { id, convId } to scroll to once that chat has loaded

  const globalQuery = query.trim();
  const showGlobal = globalQuery.length >= 2;

  useEffect(() => {
    if (!showGlobal || !hasToken) {
      setGlobalResults([]);
      setGlobalLoading(false);
      return;
    }

    const controller = new AbortController();
    setGlobalLoading(true);

    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosPrivate.get('/api/messages/search-all', {
          params: { q: globalQuery },
          signal: controller.signal,
        });
        setGlobalResults(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Global search failed', err);
        }
      } finally {
        if (!controller.signal.aborted) setGlobalLoading(false);
      }
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [globalQuery, showGlobal, hasToken, axiosPrivate]);

  // Open the chat of a result, then jump to the message
  const openSearchResult = async (r) => {
    setShowChatOnMobile(true);
    if (r.convId === convIdRef.current) {
      handleJumpToMessage(r._id);
      return;
    }

    pendingJumpMsgRef.current = { id: r._id, convId: r.convId };
    await openConversation(r.convId);
  };

  // ...and once that chat's messages are loaded, scroll to it
  useEffect(() => {
    const target = pendingJumpMsgRef.current;
    if (!target || loadingMessages || target.convId !== convId || loadedConvRef.current !== convId) return;

    pendingJumpMsgRef.current = null;
    handleJumpToMessage(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, loadingMessages]);

  const chatNameOf = (r) =>
    r.isGroup
      ? (Array.isArray(groups) ? groups.find((g) => g._id === r.convId)?.name : null) || 'Group'
      : userById[r.convId]?.name || 'Unknown user';

  // Highlight the searched text inside a result snippet
  const renderSnippet = (text, q) => {
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i === -1) return text;

    return (
      <>
        {text.slice(0, i)}
        <Box
          component="mark"
          sx={{ backgroundColor: 'rgba(255,193,7,0.45)', color: 'inherit', borderRadius: '2px', px: '1px' }}
        >
          {text.slice(i, i + q.length)}
        </Box>
        {text.slice(i + q.length)}
      </>
    );
  };

  const [loadingOlder, setLoadingOlder] = useState(false);

  const loadingOlderRef = useRef(false);
  const restoreScrollRef = useRef(null);   // scroll position to restore after prepending a page
  const pendingJumpRef = useRef(null);     // search match to scroll to once it has rendered
  const loadedConvRef = useRef(null);

  // Always-fresh values for async code
  const hasMoreRef = useRef(false);
  const convIdRef = useRef(convId);
  const convParamsRef = useRef({});
  const oldestIdRef = useRef(null);

  hasMoreRef.current = hasMore;
  convIdRef.current = convId;
  convParamsRef.current = selectedGroupId ? { groupId: selectedGroupId } : { recipientId: selectedUserId };
  oldestIdRef.current = selectedMessages[0]?._id ?? null;

  // Merge older messages into the list (de-duplicated by _id)
  const mergeOlder = (older) => {
    const byId = new Map();
    [...older, ...messagesRef.current].forEach((m) => byId.set(m._id, m));
    dispatchMessages({ type: 'SET_MESSAGE', payload: [...byId.values()] });
  };

  // One page when scrolling up, or several pages until `untilId` is loaded (search jump)
  const loadOlderPages = async ({ untilId = null, limit = PAGE_SIZE } = {}) => {
    if (loadingOlderRef.current || !hasMoreRef.current || !oldestIdRef.current) return false;

    const key = convIdRef.current;
    const params = convParamsRef.current;
    const el = messagesContainerRef.current;
    let cursor = oldestIdRef.current;
    let more = true;
    const collected = [];

    loadingOlderRef.current = true;
    setLoadingOlder(true);

    try {
      for (let i = 0; i < 50 && more && cursor; i++) {
        const { data } = await axiosPrivate.get('/api/messages', {
          params: { ...params, before: cursor, limit },
        });

        if (convIdRef.current !== key) return false;   // switched chats meanwhile

        collected.push(...data.messages);
        more = data.hasMore;
        cursor = data.messages[0]?._id;

        if (!untilId || collected.some((m) => m._id === untilId)) break;
      }

      if (!untilId && collected.length && el) {
        restoreScrollRef.current = { height: el.scrollHeight, top: el.scrollTop };
      }
      if (untilId && collected.some((m) => m._id === untilId)) {
        pendingJumpRef.current = untilId;
      }

      mergeOlder(collected);
      setHasMore(more);
      return true;
    } catch (err) {
      console.error('Unable to load older messages', err);
      return false;
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  };

  // Determine whether the message viewport is close to its bottom.
  const isAtBottom = () => {
    const el = messagesContainerRef.current;

    if (!el) return true;

    const threshold = 100;

    return (
      el.scrollHeight -
      el.scrollTop -
      el.clientHeight
    ) <= threshold;
  };

  // Scroll to the absolute bottom of the message viewport.
  const scrollToBottom = (behavior = 'auto') => {
    const el = messagesContainerRef.current;

    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior,
    });
  };

  // Hiding the list for Files/Photos loses its scroll position: come back to the newest message
  useLayoutEffect(() => {
    if (chatTab !== 'chat') return;
    stickToBottomRef.current = true;
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTab]);

  // Keep track of whether the user is following the latest message.
  const handleMessagesScroll = () => {
    stickToBottomRef.current = isAtBottom();

    const el = messagesContainerRef.current;
    if (el && !loadingMessages && el.scrollTop < 80) loadOlderPages();
  };

  // Scroll after React has committed the updated message list.
  // This handles initial loading, conversation switching,
  // incoming messages, and locally sent messages.
  useLayoutEffect(() => {
    if (!convId || loadingMessages) return;

    const el = messagesContainerRef.current;

    // An older page was just added on top: keep the viewport where it was
    if (restoreScrollRef.current && el) {
      const { height, top } = restoreScrollRef.current;
      el.scrollTop = el.scrollHeight - height + top;
      restoreScrollRef.current = null;
      return;
    }

    // A search match was just loaded: scroll to it
    if (pendingJumpRef.current) {
      const target = document.getElementById(`msg-${pendingJumpRef.current}`);
      if (target) {
        target.scrollIntoView({ block: 'center' });
        pendingJumpRef.current = null;
      }
      return;
    }

    if (stickToBottomRef.current) {
      scrollToBottom('auto');
    }
  }, [convId, selectedMessages, loadingMessages]);

  // Observe content-size changes.
  // This handles images, attachments, and delayed layout changes.
  useEffect(() => {
    const content = contentRef.current;

    if (!content || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (stickToBottomRef.current) {
        scrollToBottom('auto');
      }
    });

    observer.observe(content);

    return () => observer.disconnect();
  }, [convId]);

  useEffect(() => {
    if (selectedUserId || selectedGroupId) return;
    if (!sortedUsers.length) return;

    const latestUser = sortedUsers.find((u) => !u.isBot) || sortedUsers[0];

    stickToBottomRef.current = true;

    setSelectedUserId(latestUser._id);
    setTargetUser(latestUser);
  }, [sortedUsers, selectedUserId, selectedGroupId, setTargetUser]);

  useEffect(() => {
    if (!convId) return

    emitTypingStop()
    setDraft('')
    setReplyingTo(null) 
    setSelectedFiles([])
    setError(null)
    setMentionState(null)
    closeFind()
    setChatMenuAnchor(null)
    setDragging(false)
    dragDepthRef.current = 0
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [convId])

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!auth?._id) return;

    // socket.emit('online', auth._id);

    // const heartbeat = setInterval(() => {
    //   socket.emit('heartbeat', auth._id)}, 15000
    // );

    return () => {
      // clearInterval(heartbeat)
    };
  }, [auth?._id]);

  // --------------------------------------------------
  // Receive incoming messages in real time
  // --------------------------------------------------
  useEffect(() => {
    if (!auth?._id) return;

    const handleIncomingMessage = (message) => {
      // console.log('Socket received newMessage:', message);

      if (!message?._id) {
        console.warn('Socket message has no _id:', message);
        return;
      }

      const currentUserId = String(auth._id);

      const senderId = String(
        message.senderId?._id ||
        message.senderId ||
        message.sender?._id ||
        message.sender ||
        ''
      );

      const recipientId = String(
        message.recipientId?._id ||
        message.recipientId ||
        ''
      );

      const isIncoming = recipientId === currentUserId;
      const isOutgoing = senderId === currentUserId;

      setTypingMap((prev) => removeTyping(prev, String(message.groupId || senderId), senderId))

      if (!isIncoming && !isOutgoing) {
        console.log('Ignoring unrelated socket message:', message);
        return;
      }

      // Message for a chat that isn't open: show it as unread immediately
      if (isIncoming && message.unread) {
        const peer = String(message.groupId || senderId);
        setChatSettings((prev) => ({
          ...prev,
          [peer]: {
            ...prev[peer],
            markedUnread: true,
            unreadCount: (prev[peer]?.unreadCount || 0) + 1,
            mentionCount: (prev[peer]?.mentionCount || 0) + (message.mentioned ? 1 : 0),
          },
        }));
      }

      bumpActivity(message.groupId ? String(message.groupId) : (isOutgoing ? recipientId : senderId), message.createdAt);

      dispatchMessages({
        type: 'CREATE_MESSAGE',
        payload: message,
      });
    };

    socket.on('newMessage', handleIncomingMessage);

    return () => {
      socket.off('newMessage', handleIncomingMessage);
    };
  }, [auth?._id, dispatchMessages, bumpActivity]);

  useEffect(() => {
    setTitle('Chat');

    if (!auth?.accessToken || !convId) return;

    // A token refresh re-runs this effect: keep the pages already loaded
    if (loadedConvRef.current === convId) return;

    const controller = new AbortController();

    const loadMessages = async () => {
      loadedConvRef.current = null;
      setLoadingMessages(true);
      setHasMore(false);

      try {
        const response = await axiosPrivate.get('/api/messages', {
          params: {
            ...(selectedGroupId ? { groupId: selectedGroupId } : { recipientId: selectedUserId }),
            limit: PAGE_SIZE,
          },
          signal: controller.signal,
        });

        dispatchMessages({ type: 'SET_MESSAGE', payload: response.data.messages });
        setHasMore(response.data.hasMore);
        loadedConvRef.current = convId;
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Unable to load messages', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingMessages(false);
        }
      }
    };

    loadMessages();

    return () => controller.abort();
  }, [
    auth?.accessToken,
    convId,
    selectedGroupId,
    selectedUserId,
    axiosPrivate,
    dispatchMessages,
    setTitle
  ]);

  useEffect(() => {
    if (!auth?.accessToken) {
      return;
    }

    const controller = new AbortController();

    const getAllUsers = async () => {
      try {
        const response = await axiosPrivate.get('/api/chatUsers/users', {
          params: {
              currentUserId: auth?._id,
          },
          signal: controller.signal,
        });

        dispatch({ type: 'SET_USER', payload: response.data, });
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          return;
        }
        console.error('Chat: unable to load users', error);
      }
    };

    getAllUsers();

    socket.on('presenceUpdate', getAllUsers);          // live: someone came online or went offline
    socket.on('profileUpdated', getAllUsers);
    socket.on('chatBotChanged', getAllUsers);          // AI assistant switched on / off
    const fallback = setInterval(getAllUsers, 15000);  // safety net

    return () => {
      controller.abort();
      socket.off('presenceUpdate', getAllUsers);       // pass the handler so other listeners survive
      socket.off('profileUpdated', getAllUsers);
      socket.off('chatBotChanged', getAllUsers);
      clearInterval(fallback);
    };
  }, [auth?._id, auth?.accessToken, axiosPrivate, dispatch]);

  useEffect(() => {
    if (!auth?.accessToken) return;
    const controller = new AbortController();

    const loadGroups = async () => {
      try {
        const { data } = await axiosPrivate.get('/api/groups', { signal: controller.signal });
        
        // console.log('GET /api/groups:', data);
        // console.log('groups is array:', Array.isArray(data));
        
        setGroups(toGroupList(data));
        setGroupsLoaded(true);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Unable to load groups', err);
        }
      }
    };

    loadGroups();
    const poll = setInterval(loadGroups, 15000);   // picks up groups others add you to
    socket.on('groupsChanged', loadGroups);

    return () => { 
      controller.abort(); 
      clearInterval(poll); 
      socket.off('groupsChanged', loadGroups);
    };
  }, [auth?.accessToken, axiosPrivate]);

  // Is this browser tab visible? Messages are only marked read when somebody can actually see them.
  const [tabVisible, setTabVisible] = useState(document.visibilityState === 'visible');

  useEffect(() => {
    const onVisibility = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Mark incoming messages as read while the conversation is visible (1:1 and group)
  useEffect(() => {
    if (!tabVisible || !auth?.accessToken || !convId || !selectedMessages.length) return;

    const meId = String(auth._id);

    if (selectedGroupId) {
      const hasUnread = selectedMessages.some(
        (m) =>
          (m.type || 'text') === 'text' &&
          String(m.senderId) !== meId &&
          !(m.readBy || []).map(String).includes(meId)
      );
      if (!hasUnread) return;

      (async () => {
        try {
          await axiosPrivate.post('/api/messages/read', { groupId: selectedGroupId });

          messagesRef.current.forEach((m) => {
            if (String(m.groupId) !== selectedGroupId) return;
            if ((m.type || 'text') !== 'text' || String(m.senderId) === meId) return;
            const readBy = (m.readBy || []).map(String);
            if (!readBy.includes(meId)) {
              dispatchMessages({ type: 'UPDATE_MESSAGE', payload: { ...m, readBy: [...readBy, meId], read: true } });
            }
          });
        } catch (err) {
          console.error('Unable to sync group read receipt', err);
        }
      })();
      return;
    }

    if (!selectedUserId) return;
    const hasUnread = selectedMessages.some((msg) => msg.senderId === selectedUserId && !msg.read);
    if (!hasUnread) return;

    (async () => {
      try {
        await axiosPrivate.post('/api/messages/read', { recipientId: selectedUserId });

        messagesRef.current.forEach((m) => {
          if (!m.read && !m.groupId && String(m.senderId) === String(selectedUserId)) {
            dispatchMessages({ type: 'UPDATE_MESSAGE', payload: { ...m, read: true } });
          }
        });
      } catch (err) {
        console.error('Unable to execute read-receipt sync', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabVisible, selectedMessages.length, selectedUserId, selectedGroupId, convId, auth?.accessToken, axiosPrivate, dispatchMessages]);

  // Members (still in the group) who have read one of my group messages
  const getGroupReaders = (m) => {
    if (!selectedGroup) return [];
    const members = new Set(selectedGroup.members.map(String));
    return [...new Set((m.readBy || []).map(String))].filter(
      (id) => members.has(id) && id !== String(m.senderId)
    );
  };

  const getReadTitle = (m) => {
    if (!selectedGroupId) return 'Read';
    const readers = getGroupReaders(m);
    const total = (selectedGroup?.members.length || 1) - 1;
    if (readers.length >= total) return 'Read by everyone';
    return `Read by ${readers.map((id) => userById[id]?.name || 'Someone').join(', ')}`;
  };

  const receiptIds = useMemo(() => {
    let lastSentId = null;
    let lastReadId = null;

    for (let i = selectedMessages.length - 1; i >= 0; i--) {
      const msg = selectedMessages[i];
      const isMine = msg.senderId === auth?._id?.toString() || msg.sender === 'me';
      if (!isMine || msg.deletedAt || (msg.type && msg.type !== 'text')) continue;

      // The newest of my messages gets the "Sent" mark; keep looking only for the newest read one
      if (!lastSentId) lastSentId = msg._id;
      if (selectedGroupId) {
        if (getGroupReaders(msg).length > 0) lastReadId = msg._id;   // newest message, if anyone read it
      } else if (msg.read) {
        lastReadId = msg._id;
      }
      if (selectedGroupId) break;                       // groups: only the newest message of mine
      if (lastSentId && lastReadId) break;
    }

    return { lastSentId, lastReadId };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMessages, auth?._id, selectedGroupId, selectedGroup]);

  const handleSelectUser = (user) => {
    // Always start a newly selected conversation at the latest message.
    stickToBottomRef.current = true;
    setShowChatOnMobile(true);

    if (chatSettings[user._id]?.markedUnread) {
      updateChatSetting(user._id, { markedUnread: false });
    }
    
    setSelectedGroupId('');
    setSelectedUserId(user._id);
    setTargetUser(user);
  };

  const handleSelectGroup = (group) => {
    stickToBottomRef.current = true;
    setShowChatOnMobile(true);

    if (chatSettings[group._id]?.markedUnread) {
      updateChatSetting(group._id, { markedUnread: false });
    }

    setSelectedUserId('');
    setSelectedGroupId(group._id);
  };

  const handleSend = async () => {
    if (!hasConversation || sending) return;

    const text = draft.trim();
    if (!text && selectedFiles.length === 0) return;

    emitTypingStop()

    setSending(true);
    setError(null);
    setFileProgress({});
    stickToBottomRef.current = true;

    // One request per file. Text + reply reference go with the first message.
    const queue = selectedFiles.length > 0 ? selectedFiles : [null];
    let sent = 0;

    const controller = new AbortController();
    uploadAbortRef.current = controller;

    try {
      for (let i = 0; i < queue.length; i++) {
        const file = queue[i];
        const key = file ? fileKey(file) : null;
        const formData = new FormData();

        if (selectedGroupId) formData.append('groupId', selectedGroupId);
        else formData.append('recipientId', selectedUserId);
        formData.append('content', i === 0 ? text : '');
        if (i === 0 && replyingTo) formData.append('replyTo', replyingTo._id);
        if (i === 0) {
          const mentionIds = getMentionIds(text)
          if (mentionIds.length) formData.append('mentions', JSON.stringify(mentionIds))
        }
        if (file) formData.append('file', file);

        const response = await axiosPrivate.post('/api/messages', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: controller.signal,
          onUploadProgress: key
            ? (e) => {
                if (!e.total) return;
                // stays at 99 until the server has answered
                const percent = Math.min(99, Math.round((e.loaded * 100) / e.total));
                setFileProgress((p) => (p[key] === percent ? p : { ...p, [key]: percent }));
              }
            : undefined,
        });

        if (key) setFileProgress((p) => ({ ...p, [key]: 100 }));

        // Apply a read receipt that arrived while this request was still running
        let created = response.data;
        const earlyReaders = created?._id && earlyReadsRef.current[String(created._id)];
        if (earlyReaders) {
          delete earlyReadsRef.current[String(created._id)];
          created = created.groupId
            ? { ...created, readBy: [...new Set([...(created.readBy || []), ...earlyReaders])], read: true }
            : { ...created, read: true };
        }

        dispatchMessages({ type: 'CREATE_MESSAGE', payload: created });
        bumpActivity(convId, response.data?.createdAt);
        sent++;

        if (i === 0) {          // text and reply are now delivered
          setDraft('');
          setReplyingTo(null);
          setMentionState(null);
        }
      }

      setSelectedFiles([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Ensure the newly sent message is followed.
      stickToBottomRef.current = true;
    } catch (err) {
      // keep only the files that were NOT sent so the user can retry
      setSelectedFiles((prev) => prev.slice(sent));

      // Cancel is not an error
      if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
        setError(err.response?.data?.error || 'Unable to send message');
        console.error('Unable to send message', err);
      }
    } finally {
      uploadAbortRef.current = null;
      setSending(false);
      setFileProgress({});
    }
  };

  // Single entry point for file picker, paste and drag-and-drop
  const addFiles = (incoming) => {
    const picked = Array.from(incoming || []).filter((f) => f.size > 0);   // skips dropped folders
    if (picked.length === 0) return;

    const key = (f) => `${f.name}-${f.size}-${f.lastModified}`;
    const existing = new Set(selectedFiles.map(key));
    const merged = [...selectedFiles, ...picked.filter((f) => !existing.has(key(f)))];

    if (merged.length > MAX_FILES) {
      setError(`You can attach up to ${MAX_FILES} files at once.`);
    } else {
      setError(null);
    }
    setSelectedFiles(merged.slice(0, MAX_FILES));
  };

  const handleFileSelect = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    addFiles(picked);
  };

  // Ctrl+V with a screenshot or copied file(s). Normal text pastes are left alone.
  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!hasConversation || files.length === 0 || e.clipboardData.getData('text')) return;

    e.preventDefault();

    const extensions = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/gif': 'gif', 'image/webp': 'webp' };

    // Browsers call every pasted screenshot "image.png": give each a unique name
    addFiles(
      files.map((f, i) =>
        f.type.startsWith('image/') && (!f.name || f.name === 'image.png')
          ? new File([f], `pasted-image-${Date.now()}-${i}.${extensions[f.type] || 'png'}`, {
              type: f.type,
              lastModified: Date.now(),
            })
          : f
      )
    );
  };

  // ---------- Drag and drop ----------
  const [dragging, setDragging] = useState(false);
  const dragDepthRef = useRef(0);   // dragenter/dragleave also fire for child elements

  const dragHasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    if (hasConversation) setDragging(true);
  };

  const handleDragOver = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();                       // required, or the browser refuses the drop
    e.dataTransfer.dropEffect = hasConversation ? 'copy' : 'none';
  };

  const handleDragLeave = (e) => {
    if (!dragHasFiles(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragging(false);
  };

  const handleDrop = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();                       // stops the browser from opening the file
    dragDepthRef.current = 0;
    setDragging(false);

    if (!hasConversation || sending) return;
    addFiles(e.dataTransfer.files);
    chatInputRef.current?.focus();
  };

  const handleRemoveFile = (index) =>
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));

  const getAttachmentUrl = (message) => {

    if (!message?.attachment?.url)
      return "";

    const base = process.env.REACT_APP_SERVER_URL?.replace(/\/$/, "");

    const path = message.attachment.url.replace(/^\//, "");

    return `${base}/${path}`;
  };

  // Readable attachments from others are "new" until I open them once. The server keeps the list
  // (attachmentOpenedBy); openedIds covers what I opened since the messages were loaded.
  const [openedIds, setOpenedIds] = useState(() => new Set());

  useEffect(
    () => subscribeAttachmentOpened((id) => id && setOpenedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)))),
    []
  );

  const isNewMessageAttachment = (message) =>
    isNewAttachment(message, auth?._id?.toString()) && !openedIds.has(String(message._id));

  // First time a readable attachment is opened: tell the server and everything showing a badge
  const markAttachmentOpened = (message) => {
    if (!message?._id || !isNewMessageAttachment(message)) return;

    announceAttachmentOpened(message._id);
    axiosPrivate
      .post(`/api/messages/${message._id}/attachment-opened`)
      .catch((error) => console.error("Could not mark the document as opened", error));
  };

  // /uploads needs the login token, which a plain new tab doesn't send. So the file is fetched
  // with the token and opened from a blob URL.
  const handleOpenAttachment = async (message) => {
    if (!message?.attachment?.url) return;

    // Open the tab right away (inside the click) so the popup blocker allows it
    const tab = window.open("", "_blank");

    try {
      const response = await axiosPrivate.get(message.attachment.url, { responseType: "blob" });
      const type = (response.headers?.["content-type"] || response.data?.type || "").split(";")[0].trim();

      const viewType = getBrowserViewType(type, message.attachment.name);

      markAttachmentOpened(message);

      if (!tab || !viewType) {
        tab?.close();
        await handleDownloadAttachment(message);
        return;
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: viewType }));
      tab.opener = null;
      tab.location.href = blobUrl;
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60 * 1000); // the tab has loaded it by then
    } catch (error) {
      tab?.close();
      console.error("Open failed", error);
    }
  };

  // Photos of the open conversation, newest first (the order PhotoViewer expects). Clicking a photo
  // in the chat opens it in the viewer instead of navigating to the protected /uploads URL.
  const [viewerImageId, setViewerImageId] = useState(null);
  const chatImages = useMemo(
    () =>
      selectedMessages
        .filter((m) => m.attachment?.type?.startsWith("image/") && !m.deletedAt)
        .reverse(),
    [selectedMessages]
  );
  const viewerImageIndex = viewerImageId ? chatImages.findIndex((m) => m._id === viewerImageId) : -1;

  // The photo shown in the viewer counts as opened
  useEffect(() => {
    const m = viewerImageId ? chatImages.find((x) => x._id === viewerImageId) : null;
    if (m) markAttachmentOpened(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerImageId]);

  const handleDownloadAttachment = async (message) => {
    if (!message?.attachment?.url) return;

    try {
      const response = await axiosPrivate.get(
        message.attachment.url,
        {
          responseType: "blob",
        }
      );

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = message.attachment.name;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(blobUrl);

    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const handleCopyAttachmentLink = async (message) => {
    if (!message?.attachment?.url) return;

    const url = getAttachmentUrl(message);

    // This library handles both modern secure contexts and raw HTTP network fallback states
    const copySuccessful = copy(url, {
      debug: true,
      message: 'Press Ctrl+C to copy',
    });

    if (copySuccessful) {
      //console.log("Successfully committed link to system clipboard:", url);
    } else {
      console.error("System clipboard actions rejected by browser security profile.");
    }
  };

  // Safely formats timestamps and catches empty or invalid dates cleanly
  const formatTime = (dateString) => {
    if (!dateString) return "...";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatCallDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(s).padStart(2, '0');
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };

  // Text + icon for call / group event rows
  const getEventInfo = (m) => {
    const mine = m.senderId === auth?._id?.toString();
    const actor = mine ? 'You' : (userById[m.senderId]?.name || 'Someone');

    if (m.type === 'system') {
      const nameOf = (id) =>
        String(id) === auth?._id?.toString() ? 'you' : (userById[id]?.name || 'someone');
      const targets = (m.systemTargets || []).map(nameOf).join(', ');

      const callKind = m.call?.kind === 'video' ? 'video' : 'audio';
      const isCallEvent = m.systemAction === 'callstarted' || m.systemAction === 'callended';

      const text =
        m.systemAction === 'created' ? `${actor} created the group`
        : m.systemAction === 'left' ? `${actor} left the group`
        : m.systemAction === 'added' ? `${actor} added ${targets}`
        : m.systemAction === 'removed' ? `${actor} removed ${targets}`
        : m.systemAction === 'renamed' ? `${actor} renamed the group to "${m.systemName}"`
        : m.systemAction === 'callstarted' ? `${actor} started a ${callKind} call`
        : m.systemAction === 'callended'
          ? `${callKind === 'video' ? 'Video' : 'Audio'} call ended${m.call?.duration > 0 ? ` · ${formatCallDuration(m.call.duration)}` : ''}`
        : '';

      return {
        Icon: isCallEvent ? (callKind === 'video' ? Videocam : Call) : Groups,
        text,
        error: false,
      };
    }

    // Call
    const isVideo = m.call?.kind === 'video';
    const Icon = isVideo ? Videocam : Call;
    const label = isVideo ? 'Video call' : 'Audio call';
    const { status, duration } = m.call || {};

    if (status === 'missed') {
      return mine
        ? { Icon, text: `${label} · No answer`, error: false }
        : { Icon, text: `Missed ${label.toLowerCase()}`, error: true };
    }

    if (status === 'declined') {
      return mine
        ? { Icon, text: `${label} · Declined`, error: false }
        : { Icon, text: `You declined the ${isVideo ? 'video' : 'audio'} call`, error: false };
    }

    return {
      Icon,
      text: duration > 0 ? `${label} · ${formatCallDuration(duration)}` : `${label} ended`,
      error: false,
    };
  };

  const formatDividerDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    // Extract the time formatting string first (e.g., "2:30 PM")
    const timeStr = date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // Normalize all dates to midnight (00:00:00) for clean calendar comparisons
    const inputDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const todayObj = new Date();
    const today = new Date(todayObj.getFullYear(), todayObj.getMonth(), todayObj.getDate());
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (inputDate.getTime() === today.getTime()) {
      return `Today ${timeStr}`;
    }

    if (inputDate.getTime() === yesterday.getTime()) {
      return `Yesterday ${timeStr}`;
    }

    const dateStr = date.toLocaleDateString([], { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric'
    }).replace(/,/g, '');

    return `${dateStr} ${timeStr}`;
  };

  let lastDateStr = "";

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeMenuMessage, setActiveMenuMessage] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState(null);
  const [attachmentMenuMessage, setAttachmentMenuMessage] = useState(null);

  const handleOpenAttachmentMenu = (event, message) => {
    event.stopPropagation();

    setAttachmentMenuAnchor(event.currentTarget);
    setAttachmentMenuMessage(message);
  };

  const handleCloseAttachmentMenu = () => {
    setAttachmentMenuAnchor(null);
    setAttachmentMenuMessage(null);
  };

  // Opens the menu anchor panel tied to the specific message item
  const handleOpenMenu = (event, message) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveMenuMessage(message);
  };

  // Closes the menu panel and cleans structural active selections
  const handleCloseMenu = () => {
    setActiveMenuMessage(null);

    // Force focus out of the menu before closing it
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    
    setMenuAnchorEl(null); 
  };

  // Action dispatch stubs for you to map to your backend endpoints
  const handleActionClick = (actionType, message) => {
    // safely lifting focus off the MenuItem before its wrapper div becomes aria-hidden.
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }

    if (actionType === 'delete') {
      handleDelete(message._id);
    } else if (actionType === 'edit' && message) {
      setEditingMessageId(message._id);
      setEditDraft(message.content);
    } else if (actionType === 'forward') {
      openForwardDialog(message);
    } else if (actionType === 'reply') {
      setReplyingTo(message);
      setTimeout(() => chatInputRef.current?.focus(), 0);
    } else if (actionType === 'pin') {
      handleTogglePin(message);
    }
    
    setTappedMessageId(null);
    handleCloseMenu();
  };

  // State to track the ID of the message currently being edited inline
  const [editingMessageId, setEditingMessageId] = useState(null);
  // State to hold the temporary text typed while editing
  const [editDraft, setEditDraft] = useState('');
  // Track updating status spinner loader
  const [updatingMessage, setUpdatingMessage] = useState(false);

  // Cancels the current active editing mode and clears draft text pools
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditDraft('');
  };

  const handleSaveEdit = async (messageId) => {
    const text = editDraft.trim();
    if (!text) return;

    setUpdatingMessage(true);
    try {
      const response = await axiosPrivate.patch(`/api/messages/${messageId}`, { content: text });
      
      dispatchMessages({ type: 'UPDATE_MESSAGE',  payload: response.data });
      
      setEditingMessageId(null);
      setEditDraft('');
    } catch (error) {
      console.error("Unable to update message content", error);
    } finally {
      setUpdatingMessage(false);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      // Send soft-delete toggle request to your backend path router
      const response = await axiosPrivate.delete(`/api/messages/${messageId}`);
      
      // Update global context using our inline mapping reducer 
      dispatchMessages({ type: 'UPDATE_MESSAGE', payload: response.data });
    } catch (err) {
      console.error("Unable to change message deletion status", err);
    }
  };

  const handleSelectEmoji = (emojiChar) => {
    // Appends the chosen emoji directly to the current input string
    setDraft((prevDraft) => prevDraft + emojiChar);
    
    // Optional: refocus the text field so the user can keep typing immediately
    chatInputRef.current?.focus();
  };

  // ---------- Typing indicator (sending side) ----------
  const emitTyping = () => {
    if (!convId) return

    const now = Date.now()
    if (now - lastTypingEmitRef.current < 2500) return   // at most one event every 2.5s
    lastTypingEmitRef.current = now

    const target = selectedGroupId ? { groupId: selectedGroupId } : { toUserId: selectedUserId }
    typingTargetRef.current = target
    socket.emit('typing', target)
  }

  const emitTypingStop = () => {
    lastTypingEmitRef.current = 0
    if (!typingTargetRef.current) return

    socket.emit('typing:stop', typingTargetRef.current)
    typingTargetRef.current = null
  }

  const handleDraftChange = (e) => {
    const value = e.target.value
    setDraft(value)
    updateMentionState(value, e.target.selectionStart)

    if (value.trim()) emitTyping()
    else emitTypingStop()
  }

  // ---------- Reactions ----------
  const handleToggleReaction = async (messageId, emoji) => {
    const meId = auth?._id?.toString()
    const original = messagesRef.current.find((m) => m._id === messageId)
    if (!original || !meId) return

    // Instant update, then confirm with the server
    dispatchMessages({
      type: 'UPDATE_MESSAGE',
      payload: { ...original, reactions: toggleLocalReaction(original.reactions, emoji, meId) },
    })

    try {
      const { data } = await axiosPrivate.put(`/api/messages/${messageId}/reactions`, { emoji })
      const latest = messagesRef.current.find((m) => m._id === messageId) || original
      dispatchMessages({ type: 'UPDATE_MESSAGE', payload: { ...latest, reactions: data.reactions } })
    } catch (err) {
      const latest = messagesRef.current.find((m) => m._id === messageId) || original
      dispatchMessages({ type: 'UPDATE_MESSAGE', payload: { ...latest, reactions: original.reactions || [] } })
      setError(err.response?.data?.error || 'Unable to react to message')
    }
  }

  // Resolve the original message for a reply (works if backend populates OR only sends an id)
  const getRepliedMessage = (message) => {
    if (!message?.replyTo) return null;

    const isObj = typeof message.replyTo === 'object';
    const id = isObj ? message.replyTo._id : message.replyTo;

    // live copy first (reflects edits / deletes), populated snapshot as fallback
    return messages.find((m) => m._id === id) || (isObj ? message.replyTo : null);
  };

  const getPreviewText = (m) => {
    if (!m) return 'Original message unavailable';
    if (m.deletedAt) return 'This message has been deleted.';
    if (m.content) return m.content;
    if (m.attachment?.name) return `📎 ${m.attachment.name}`;
    return '';
  };

  // getSenderName: handle group senders
  const getSenderName = (m) => {
    if (!m) return '';
    if (m.senderId === auth?._id?.toString()) return 'You';
    return userById[m.senderId]?.name ?? selectedUser?.name;
  };

  const handleJumpToMessage = async (id) => {
    stickToBottomRef.current = false;

    // Not loaded yet: page back until it is (the layout effect scrolls to it after render)
    if (!messagesRef.current.some((m) => m._id === id)) {
      const ok = await loadOlderPages({ untilId: id, limit: 100 });
      if (!ok) return;
    } else {
      document.getElementById(`msg-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setHighlightedId(id);
    setTimeout(() => setHighlightedId(null), 1500);
  };

  const openForwardDialog = (message) => {
    setForwardMessage(message);
    setForwardTargets([]);
    setForwardQuery('');
    setForwardNote('');
  };

  const closeForwardDialog = () => {
    if (forwarding) return;
    setForwardMessage(null);
  };

  const toggleForwardTarget = (id) =>
    setForwardTargets((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleForward = async () => {
    if (!forwardMessage || forwardTargets.length === 0) return;
    setForwarding(true);
    try {
      const { data } = await axiosPrivate.post('/api/messages/forward', {
        messageId: forwardMessage._id,
        recipientIds: forwardTargets,
        note: forwardNote.trim(),
      });
      // data = array of created messages; show only those in the open conversation
      (Array.isArray(data) ? data : [data])
        .filter((m) => m.recipientId === selectedUserId)
        .forEach((m) => dispatchMessages({ type: 'CREATE_MESSAGE', payload: m }));
      setForwardMessage(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to forward message');
      console.error('Unable to forward message', err);
    } finally {
      setForwarding(false);
    }
  };

  const forwardableUsers = useMemo(
    () => (users || []).filter((u) =>
      !u.isBot && u._id !== auth?._id && u.name?.toLowerCase().includes(forwardQuery.toLowerCase())),
    [users, auth?._id, forwardQuery]
  );

  // People and groups the "Send files" dialog can send to
  const fileRecipients = useMemo(
    () => (users || []).filter((u) => !u.isBot && u._id !== auth?._id),
    [users, auth?._id]
  );

  // Messages sent from the "Send files" dialog: show the ones in the open chat, bump the rest
  const handleFilesSent = (created) => {
    created.forEach((m) => {
      const peer = m.groupId ? String(m.groupId) : String(m.recipientId);
      bumpActivity(peer, m.createdAt);
      const inOpenChat = selectedGroupId
        ? String(m.groupId) === selectedGroupId
        : !m.groupId && String(m.recipientId) === selectedUserId;
      if (inOpenChat) dispatchMessages({ type: 'CREATE_MESSAGE', payload: m });
    });
    stickToBottomRef.current = true;
  };

  useEffect(() => {
    setActiveChatUserId(convId || null);

    const reportActive = () => {
      const visible = document.visibilityState === 'visible';
      socket.emit('activeChat', visible ? (convId || null) : null);
    };

    reportActive();

    const onVisibility = () => {
      reportActive();

      // Came back to the tab: whatever arrived while it was hidden is now seen
      if (
        document.visibilityState === 'visible' &&
        convId &&
        chatSettingsRef.current[convId]?.markedUnread
      ) {
        updateChatSetting(convId, { markedUnread: false });
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    socket.on('connect', reportActive);        // re-report after a reconnect

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      socket.off('connect', reportActive);
      socket.emit('activeChat', null);
      setActiveChatUserId(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  // Select a user OR a group by id
  const openConversation = async (id) => {
    const userList = Array.isArray(users) ? users : [];

    const user = userList.find((u) => u._id === id);

    if (user) {
      handleSelectUser(user);
      return;
    }

    let group = Array.isArray(groups)
      ? groups.find((g) => g._id === id)
      : null;

    // The group may be new and not in the 15s poll yet: fetch once
    if (!group) {
      try {
        const { data } = await axiosPrivate.get('/api/groups');

        const groupList = toGroupList(data)

        // console.log('Groups response:', data);
        // console.log('Normalized groups:', groupList);

        group = groupList.find((g) => g._id === id) || null;
      } catch (err) {
        console.error('Unable to load groups', err);
      }
    }

    if (group) handleSelectGroup(group);
  };

  // Clicked a notification while Chat was closed
  useEffect(() => {
    if (!users?.length || !groupsLoaded) return;
    const pending = consumePendingChat();
    if (pending) openConversation(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, groupsLoaded]);

  // Clicked a notification while Chat is already open
  useEffect(() => {
    const onOpen = (e) => {
      consumePendingChat();
      openConversation(e.detail);
    };
    window.addEventListener('chat:open', onOpen);
    return () => window.removeEventListener('chat:open', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, groups, chatSettings]);

  // Opened from the desktop client: /chat?open=<id>&call=audio|video
  useEffect(() => {
    if (!users?.length || !groupsLoaded) return;

    const params = new URLSearchParams(window.location.search);
    const target = params.get('open');
    if (!target) return;

    const kind = params.get('call');
    window.history.replaceState({}, '', window.location.pathname);   // run only once

    openConversation(target);

    if (inCall || (kind !== 'audio' && kind !== 'video')) return;
    const user = users.find((u) => u._id === target);
    const group = groups.find((g) => g._id === target);
    if (user) startCall(user, kind);
    else if (group) startGroupCall(group, kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, groupsLoaded]);

  const openGroupDialog = () => {
    setGroupName('');
    setGroupQuery('');
    setGroupMembers(selectedUserId ? [selectedUserId] : []);   // pre-add the current 1:1 contact
    setGroupDialogOpen(true);
  };

  const toggleGroupMember = (id) =>
    setGroupMembers((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleCreateGroup = async () => {
    setCreatingGroup(true);
    try {
      const { data } = await axiosPrivate.post('/api/groups', {
        name: groupName.trim(),
        memberIds: groupMembers,
      });
      setGroups((prev) => [data, ...prev]);
      setGroupDialogOpen(false);
      handleSelectGroup(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const groupCandidates = useMemo(
    () => (users || []).filter((u) =>
      u._id !== auth?._id && u.name?.toLowerCase().includes(groupQuery.toLowerCase())),
    [users, auth?._id, groupQuery]
  );

  const filteredGroups = useMemo(() => {
    const list = Array.isArray(groups) ? groups : [];
    const q = query.trim().toLowerCase();

    return q
      ? list.filter((g) =>
          String(g?.name || '').toLowerCase().includes(q)
        )
      : list;
  }, [groups, query]);

  // One "Recent" list like Teams: groups and people mixed, latest message first
  const chatItems = useMemo(() => {
    const timeOf = (id, at) => Math.max(at ? new Date(at).getTime() : 0, activityAt[id] || 0);

    return [
      ...filteredGroups.map((g) => ({ kind: 'group', id: g._id, item: g, time: timeOf(g._id, g.lastMessageAt) })),
      ...contactUsers.map((u) => ({ kind: 'user', id: u._id, item: u, time: timeOf(u._id, u.lastMessageAt) })),
    ].sort((a, b) => {
      if (a.time !== b.time) return b.time - a.time;
      // No messages yet: online people first, then alphabetical
      const aOnline = a.kind === 'user' && a.item.isOnline;
      const bOnline = b.kind === 'user' && b.item.isOnline;
      if (aOnline !== bOnline) return aOnline ? -1 : 1;
      return (a.item.name || '').localeCompare(b.item.name || '');
    });
  }, [filteredGroups, contactUsers, activityAt]);

  useEffect(() => {
    if (!convId || !settingsLoaded) return;

    if (
      document.visibilityState === 'visible' &&
      chatSettingsRef.current[convId]?.markedUnread
    ) {
      updateChatSetting(convId, { markedUnread: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, settingsLoaded]);

  // Call history and group events (no popup, no unread logic for the sender)
  useEffect(() => {
    if (!auth?._id) return;

    const handleChatEvent = (message) => {
      if (!message?._id) return;

      // Missed call while this chat was not open: show the unread dot immediately
      if (message.unread) {
        const peer = String(message.groupId || message.senderId);
        setChatSettings((prev) => ({
          ...prev,
          [peer]: { ...prev[peer], markedUnread: true },
        }));
      }

      dispatchMessages({ type: 'CREATE_MESSAGE', payload: message });
    };

    socket.on('chatEvent', handleChatEvent);
    return () => socket.off('chatEvent', handleChatEvent);
  }, [auth?._id, dispatchMessages]);

  // If the open group is no longer in my list (e.g. removed by the owner), close it
  useEffect(() => {
    if (!selectedGroupId || !groupsLoaded) return;
    if (groups.some((g) => g._id === selectedGroupId)) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await axiosPrivate.get('/api/groups');
        if (cancelled) return;

        const list = toGroupList(data)
        if (list.some((g) => g._id === selectedGroupId)) {
          setGroups(list);
          return;
        }

        setMembersOpen(false);
        setSelectedGroupId('');
      } catch (err) {
        console.error('Unable to verify group membership', err);
      }
    })();

    return () => { cancelled = true; };
  }, [groups, selectedGroupId, groupsLoaded, axiosPrivate]);

  // Is a call running in the group I just opened?
  useEffect(() => {
    if (selectedGroupId) refreshGroupCall(selectedGroupId)
  }, [selectedGroupId, refreshGroupCall])

  // And in all my groups (for the sidebar indicator), once they are loaded
  useEffect(() => {
    if (!groupsLoaded) return
    groups.forEach((g) => refreshGroupCall(g._id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsLoaded, groups.length])

  // Someone is typing / stopped typing
  useEffect(() => {
    if (!auth?._id) return

    const onTyping = ({ convId: key, fromUserId, name }) => {
      setTypingMap((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), [fromUserId]: { name, until: Date.now() + 4000 } },
      }))
    }

    const onTypingStop = ({ convId: key, fromUserId }) => {
      setTypingMap((prev) => removeTyping(prev, key, fromUserId))
    }

    socket.on('typing', onTyping)
    socket.on('typing:stop', onTypingStop)

    return () => {
      socket.off('typing', onTyping)
      socket.off('typing:stop', onTypingStop)
    }
  }, [auth?._id])

  // Forget people who stopped typing without telling us (4 seconds without a new event)
  useEffect(() => {
    const timer = setInterval(() => {
      setTypingMap((prev) => {
        const now = Date.now()
        let changed = false
        const next = {}

        for (const [key, people] of Object.entries(prev)) {
          const kept = Object.fromEntries(Object.entries(people).filter(([, t]) => t.until > now))
          if (Object.keys(kept).length !== Object.keys(people).length) changed = true
          if (Object.keys(kept).length > 0) next[key] = kept
        }

        return changed ? next : prev
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // The other person read my messages: turn "Sent" into "Read" right away
  useEffect(() => {
    if (!auth?._id) return;

    const onRead = ({ readerId, groupId, messageIds }) => {
      const meId = String(auth._id);
      const reader = String(readerId);

      const ids = new Set((messageIds || []).map(String));

      // Read before my send request came back: remember it for handleSend
      const known = new Set(messagesRef.current.map((m) => String(m._id)));
      ids.forEach((id) => {
        if (known.has(id)) return;
        earlyReadsRef.current[id] = [...new Set([...(earlyReadsRef.current[id] || []), reader])];
      });

      // Group: add the reader to readBy of my messages in that group
      if (groupId) {

        messagesRef.current.forEach((m) => {
          if (String(m.groupId) !== String(groupId) || String(m.senderId) !== meId) return;
          if (ids.size && !ids.has(String(m._id))) return;

          const readBy = (m.readBy || []).map(String);
          if (readBy.includes(reader)) return;

          dispatchMessages({ type: 'UPDATE_MESSAGE', payload: { ...m, readBy: [...readBy, reader], read: true } });
        });
        return;
      }

      // 1:1
      messagesRef.current.forEach((m) => {
        if (!m.read && !m.groupId && String(m.senderId) === meId && String(m.recipientId) === reader) {
          dispatchMessages({ type: 'UPDATE_MESSAGE', payload: { ...m, read: true } });
        }
      });
    };

    socket.on('messagesRead', onRead);
    return () => socket.off('messagesRead', onRead);
  }, [auth?._id, dispatchMessages]);

  // Someone reacted to a message in one of my chats
  useEffect(() => {
    if (!auth?._id) return

    const onReaction = ({ messageId, reactions }) => {
      const m = messagesRef.current.find((x) => x._id === messageId)
      if (m) dispatchMessages({ type: 'UPDATE_MESSAGE', payload: { ...m, reactions } })
    }

    socket.on('reaction', onReaction)
    return () => socket.off('reaction', onReaction)
  }, [auth?._id, dispatchMessages])

  const overallPercent = selectedFiles.length
    ? Math.round(selectedFiles.reduce((sum, f) => sum + (fileProgress[fileKey(f)] ?? 0), 0) / selectedFiles.length)
    : 0;

  // Chat list row, Teams style: accent bar on the open chat, name + time on top, details below
  const chatRowSx = {
    alignItems: 'center',
    position: 'relative',
    mx: 1,
    my: 0.25,
    px: 1.25,
    borderRadius: 1.5,
    '&:hover': { backgroundColor: alpha(accent, 0.08) },
    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: alpha(accent, 0.16) },
    '&.Mui-selected::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 10,
      bottom: 10,
      width: 3,
      borderRadius: 2,
      backgroundColor: accent,
    },
  };

  // "More chat options" menu: same look as the account menu in the Navbar
  const chatMenuPaperSx = {
    mt: 0.75,
    minWidth: 260,
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
    borderRadius: 2,
    boxShadow: 8,
    '& .MuiMenu-list': { py: 0.5 },
    '& .MuiMenuItem-root': {
      mx: 0.5,
      px: 1.5,
      py: 0.9,
      borderRadius: 1.5,
      fontSize: '0.875rem',
      '&:hover': { backgroundColor: alpha(accent, 0.12) },
      '&.Mui-disabled': { opacity: 0.5 },
    },
    '& .MuiListItemIcon-root': { color: accent, minWidth: 34 },
    '& .MuiListItemText-primary': { fontSize: '0.875rem' },
    '& .MuiListItemText-secondary': { fontSize: '0.72rem' },
  };

  const chatMenuSectionSx = {
    px: 2,
    pt: 1,
    pb: 0.5,
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  };

  const chatMenuDangerSx = {
    color: theme.palette.error.main,
    '& .MuiListItemIcon-root': { color: `${theme.palette.error.main} !important` },
    '&:hover': { backgroundColor: `${alpha(theme.palette.error.main, 0.1)} !important` },
  };

  // Chat / Files / Photos, like the tabs next to the chat name in Teams
  const renderChatTabs = () => (
    <Tabs
      value={chatTab}
      onChange={(_, v) => setChatTab(v)}
      textColor="inherit"
      sx={{
        minHeight: 40,
        '& .MuiTabs-indicator': { backgroundColor: accent, height: 3, borderRadius: '3px 3px 0 0' },
      }}
    >
      {[
        { value: 'chat', label: 'Chat', fresh: 0 },
        { value: 'files', label: 'Files', fresh: newDocs.files },
        { value: 'photos', label: 'Photos', fresh: newDocs.photos },
      ].map((t) => (
        <Tab
          key={t.value}
          value={t.value}
          label={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              {t.label}
              {t.fresh > 0 && (
                <Box
                  component="span"
                  title={`${t.fresh} new ${t.fresh === 1 ? 'document' : 'documents'}`}
                  sx={{
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    borderRadius: '9px',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    lineHeight: '18px',
                    textAlign: 'center',
                    color: theme.palette.getContrastText(accent),
                    backgroundColor: accent,
                  }}
                >
                  {t.fresh > 99 ? '99+' : t.fresh}
                </Box>
              )}
            </Box>
          }
          disableRipple
          sx={{
            minHeight: 40,
            minWidth: 0,
            px: 1.5,
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 600,
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary },
            '&.Mui-selected': { color: theme.palette.text.primary },
          }}
        />
      ))}
    </Tabs>
  );

  // Icon buttons in the message hover bar
  const toolboxIconSx = {
    width: 30,
    height: 30,
    color: theme.palette.text.secondary,
    '&:hover': { color: accent, backgroundColor: alpha(accent, 0.12) },
  };

  const roleChipSx = (role) => ({
    height: 18,
    fontWeight: 600,
    fontSize: '0.65rem',
    flexShrink: 0,
    '& .MuiChip-label': { px: 0.75 },
    // Same look as the Role column in the Users table
    color: role === ROLES.Root ? accentText : accent,
    borderColor: alpha(accent, 0.5),
    backgroundColor: role === ROLES.Root ? accent : alpha(accent, 0.08),
  });

  const renderChatRow = ({ id, selected, onClick, avatar, name, badges, secondary, time, icons }) => {
    const unread = chatSettings[id]?.markedUnread;

    return (
      <ListItemButton key={id} selected={selected} onClick={onClick} sx={chatRowSx}>
        {avatar}
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <Typography
              variant="body1"
              noWrap
              sx={{ color: theme.palette.text.primary, fontWeight: unread ? 700 : 500, minWidth: 0 }}
            >
              {name}
            </Typography>
            {badges}
            <Typography
              variant="caption"
              sx={{
                ml: 'auto',
                pl: 1,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                color: unread ? accent : theme.palette.text.secondary,
                fontWeight: unread ? 700 : 400,
              }}
            >
              {formatChatTime(time)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" minWidth={0} mt={0.25}>
            <Typography
              variant="body2"
              noWrap
              sx={{ flex: 1, minWidth: 0, color: unread ? theme.palette.text.primary : theme.palette.text.secondary }}
            >
              {secondary}
            </Typography>
            {icons}
            {chatSettings[id]?.muted && (
              <NotificationsOff sx={{ fontSize: '1rem', color: theme.palette.text.secondary, ml: 0.5 }} />
            )}
            {renderUnread(id)}
          </Box>
        </Box>
      </ListItemButton>
    );
  };

  const renderContactRow = (user, time) => renderChatRow({
    id: user._id,
    selected: user._id === selectedUserId,
    onClick: () => handleSelectUser(user),
    avatar: user.isBot
      ? <Avatar sx={{ bgcolor: accent, color: accentText, mr: 1.5 }}><SmartToy /></Avatar>
      : <UserAvatar selectedUser={user} />,
    name: user.name,
    badges: user.isBot ? (
      <Box
        component="span"
        sx={{
          px: 0.75, borderRadius: '999px', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
          color: accent, border: `1px solid ${alpha(accent, 0.6)}`,
        }}
      >
        BOT
      </Box>
    ) : (
      [].concat(user.roles ?? []).map((role) => (
        <Chip key={role} size="small" variant="outlined" label={role} sx={roleChipSx(role)} />
      ))
    ),
    secondary: user.isBot ? 'Ask me anything' : (user.email || 'No email available'),
    time,
  });

  const renderGroupRow = (group, time) => renderChatRow({
    id: group._id,
    selected: group._id === selectedGroupId,
    onClick: () => handleSelectGroup(group),
    avatar: <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 1.5 }}><Groups /></Avatar>,
    name: group.name,
    secondary: `${group.members.length} members`,
    icons: activeGroupCalls[group._id] && (
      <Tooltip title="Call in progress">
        <Videocam sx={{ fontSize: '1rem', color: theme.palette.success.main, ml: 0.5 }} />
      </Tooltip>
    ),
    time,
  });

  const typingNames = Object.values(typingMap[convId] || {}).map((t) => t.name)
  const typingText =
    typingNames.length === 0 ? ''
    : typingNames.length === 1 ? `${typingNames[0]} is typing`
    : typingNames.length === 2 ? `${typingNames[0]} and ${typingNames[1]} are typing`
    : `${typingNames.length} people are typing`

  // Quick access in the header: the people you talked to last (then who's online)
  const isGuestUser = [].concat(auth?.roles ?? []).includes(ROLES.Guest);
  const quickPeople = sortedUsers.filter((u) => !u.isBot).slice(0, 8);

  // Contacts online / offline, for the header pills (the assistant bot isn't counted)
  const contactStats = (() => {
    const people = sortedUsers.filter((u) => !u.isBot)
    const online = people.filter((u) => u.isOnline).length
    return { total: people.length, online, offline: people.length - online }
  })()

  return (
    <Box sx={{ ...PAGE_SX, p: { xs: 1, md: '1.5rem' } }}>
      {/* Header like the other pages (hidden on phones: the chat needs the room).
          Guests can fold it away with the button in the Chats panel. */}
      {!isMobile && (
        <Collapse in={!(headerHidden)} timeout={250} sx={{ flexShrink: 0 }}>
        <FlexBetween sx={{ mb: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
          <Header title="CHAT" subtitle="Messages, groups and calls with other users" />

          {/* Quick chat: fills the wide header when there is room (Guests have no sidebar) */}
          {quickPeople.length > 0 && (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: { xs: 'none', lg: isGuestUser ? 'flex' : 'none', xl: 'flex' },
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  minWidth: 0,
                  maxWidth: '100%',
                  px: 2,
                  py: 0.75,
                  borderRadius: 3,
                  overflow: 'hidden',
                  backgroundColor: alpha(theme.palette.background.alt, 0.6),
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                }}
              >
                <Box sx={{ flexShrink: 0, pr: 1.5, borderRight: `1px solid ${theme.palette.divider}` }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.palette.secondary.main }}>
                    Quick chat
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    Recent &amp; online
                  </Typography>
                </Box>

                {/* py leaves room for the hover lift and ring; overflow would crop them otherwise */}
                <Box sx={{ display: 'flex', gap: 1, minWidth: 0, overflow: 'hidden', py: 0.75, my: -0.75 }}>
                  {quickPeople.map((u) => {
                    const active = !selectedGroupId && selectedUserId === u._id;
                    const firstName = String(u.name || '').split(/\s+/)[0];
                    return (
                      <Tooltip key={u._id} title={`Chat with ${u.name}${u.isOnline ? ' · online' : ''}`} arrow>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSelectUser(u)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSelectUser(u); }}
                          sx={{
                            width: 56,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.4,
                            py: 0.25,
                            borderRadius: 2,
                            cursor: 'pointer',
                            outline: 'none',
                            transition: 'transform .15s ease',
                            '&:hover, &:focus-visible': { transform: 'translateY(-2px)' },
                            '&:hover .quick-avatar, &:focus-visible .quick-avatar': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.secondary.main, 0.6)}`,
                            },
                          }}
                        >
                          <Box sx={{ position: 'relative' }}>
                            <Avatar
                              className="quick-avatar"
                              src={avatarSrc(u.avatar)}
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: '0.85rem',
                                ...avatarColor(u.name, theme.palette.mode),
                                transition: 'box-shadow .15s ease',
                                boxShadow: active ? `0 0 0 2px ${theme.palette.secondary.main}` : 'none',
                              }}
                            >
                              {getInitials(u.name)}
                            </Avatar>
                            <Box
                              sx={{
                                position: 'absolute',
                                right: -1,
                                bottom: -1,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: u.isOnline ? '#35C759' : '#9E9E9E',
                                border: `2px solid ${theme.palette.background.alt}`,
                              }}
                            />
                          </Box>
                          <Typography
                            noWrap
                            sx={{
                              maxWidth: '100%',
                              fontSize: '0.68rem',
                              fontWeight: active ? 700 : 500,
                              color: active ? theme.palette.secondary.main : theme.palette.text.secondary,
                            }}
                          >
                            {firstName}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            {isRoot && (
              <Tooltip
                arrow
                title={botEnabled
                  ? 'AI assistant is on: every user sees it in Chats. Click to turn it off.'
                  : 'AI assistant is off: hidden from every user. Click to turn it on.'}
              >
                {/* Pill like the online counters; the whole pill toggles */}
                <Box
                  role="switch"
                  aria-checked={botEnabled}
                  aria-label="AI assistant"
                  tabIndex={0}
                  onClick={() => !botSaving && toggleChatBot()}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !botSaving) {
                      e.preventDefault();
                      toggleChatBot();
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    height: 32,
                    pl: 1.25,
                    pr: 0.5,
                    borderRadius: '999px',
                    cursor: botSaving ? 'progress' : 'pointer',
                    userSelect: 'none',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: botEnabled ? theme.palette.text.primary : theme.palette.text.secondary,
                    backgroundColor: botEnabled ? alpha(theme.palette.secondary.main, 0.1) : 'transparent',
                    border: `1px solid ${botEnabled ? alpha(theme.palette.secondary.main, 0.5) : theme.palette.divider}`,
                    transition: 'background-color .15s ease, border-color .15s ease',
                    '&:hover, &:focus-visible': {
                      outline: 'none',
                      borderColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <SmartToy sx={{ fontSize: 17, color: botEnabled ? theme.palette.secondary.main : theme.palette.text.secondary }} />
                  AI assistant
                  <Switch
                    size="small"
                    checked={botEnabled}
                    disabled={botSaving}
                    tabIndex={-1}
                    inputProps={{ 'aria-hidden': true }}
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiSwitch-switchBase.Mui-checked': { color: theme.palette.secondary.main },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: theme.palette.secondary.main,
                        opacity: 0.5,
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            )}
            <OnlineCountPills
              total={contactStats.total}
              online={contactStats.online}
              offline={contactStats.offline}
              totalIcon={<Groups sx={{ fontSize: 16, color: theme.palette.secondary.main }} />}
            />
          </Box>
        </FlexBetween>
        </Collapse>
      )}

    {/* The two panels. CallDialog positions itself over this element (by id). */}
    <Box
      id="chat-page-root"
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      gap={2}
      flex={1}
      minHeight={0}
      overflow="hidden"
    >
      <GlobalStyles
        styles={{
          '::highlight(find-match)': {
            backgroundColor: 'rgba(255,193,7,0.45)',
            color: 'inherit',
          },
          '::highlight(find-current)': {
            backgroundColor: 'rgba(255,152,0,0.9)',
            color: '#000000',
          },
          '::highlight(mention)': {
            backgroundColor: 'rgba(25,118,210,0.28)',
            color: 'inherit',
          },
          '::highlight(mention-me)': {
            backgroundColor: 'rgba(244,67,54,0.32)',
            color: 'inherit',
          },
        }}
      />

      <Paper
        elevation={0}
        sx={{
          width: { xs: '100%', md: 320 },
          height: '100%',
          display: { xs: showChatOnMobile ? 'none' : 'flex', md: 'flex' },
          flexDirection: 'column',
          flexShrink: 0,
          borderRadius: '0.55rem',   // same corners as the grid cards on other pages
          overflow: 'hidden',
          backgroundColor: theme.palette.background.alt,
          color: theme.palette.text.primary,
          border: gridBorder,
        }}
      >
        <Box px={2} pt={2} pb={1.5} display="flex" alignItems="baseline" gap={1}>
          <Typography variant="h4" sx={{ color: theme.palette.secondary[200], fontWeight: 600 }}>
            Chats
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            ({contactStats.total + groups.length})
          </Typography>

          {!isMobile && (
            <Tooltip title={headerHidden ? 'Show page header' : 'Hide page header'} arrow>
              <IconButton
                size="small"
                onClick={toggleHeader}
                aria-label={headerHidden ? 'Show page header' : 'Hide page header'}
                aria-expanded={!headerHidden}
                sx={{
                  ml: 'auto',
                  alignSelf: 'center',
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  color: headerHidden ? theme.palette.secondary.main : theme.palette.text.secondary,
                  border: `1px solid ${headerHidden ? alpha(theme.palette.secondary.main, 0.5) : theme.palette.divider}`,
                  backgroundColor: headerHidden ? alpha(theme.palette.secondary.main, 0.1) : 'transparent',
                  '&:hover': { color: theme.palette.secondary.main, borderColor: alpha(theme.palette.secondary.main, 0.6) },
                }}
              >
                {headerHidden ? <UnfoldMore sx={{ fontSize: 18 }} /> : <UnfoldLess sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box px={2} pb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts and messages"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 36,
                borderRadius: 2,
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.alt,
                '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' },
                '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)' },
                '&.Mui-focused fieldset': { borderColor: accent, borderWidth: 1 },
              },
              '& .MuiInputBase-input::placeholder': { color: theme.palette.text.secondary, opacity: 0.9 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider />

        <Box
          flex={1}
          overflow="auto"
          sx={{
            ...scrollbarSx,
          }}
        >
          <List disablePadding>   
            {botUser && renderContactRow(botUser, activityAt[botUser._id] || botUser.lastMessageAt)}

            {chatItems.length === 0 && !botUser && !showGlobal && (
              <Box p={3}>
                <Typography color="text.secondary">No contacts found.</Typography>
              </Box>
            )}

            {chatItems.map(({ kind, item, time }) =>
              kind === 'group' ? renderGroupRow(item, time) : renderContactRow(item, time)
            )}
          </List>

          {showGlobal && (
            <Box>
              <Divider />
              <Typography
                variant="overline"
                sx={{ px: 2, pt: 1.5, display: 'block', fontWeight: 700, color: theme.palette.text.secondary }}
              >
                Messages
              </Typography>

              {globalLoading && globalResults.length === 0 ? (
                <Box textAlign="center" py={2}>
                  <CircularProgress size={18} />
                </Box>
              ) : globalResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                  No messages found.
                </Typography>
              ) : (
                <List disablePadding>
                  {globalResults.map((r) => {
                    const senderName = r.senderId === auth?._id ? 'You' : (userById[r.senderId]?.name || 'Someone');

                    return (
                      <ListItemButton
                        key={r._id}
                        alignItems="flex-start"
                        selected={r.convId === convId}
                        onClick={() => openSearchResult(r)}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1}>
                              <Typography variant="body2" noWrap sx={{ fontWeight: 700, minWidth: 0 }}>
                                {chatNameOf(r)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {new Date(r.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                              }}
                            >
                              {senderName}: {renderSnippet(r.snippet, globalQuery)}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    );
                  })}

                  {globalResults.length >= 50 && (
                    <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1.5, display: 'block' }}>
                      Showing the 50 most recent matches.
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <Box
        flex={1}
        minWidth={0}
        flexDirection="column"
        height="100%"
        sx={{ display: { xs: showChatOnMobile ? 'flex' : 'none', md: 'flex' } }}
      >
        <Paper
          elevation={0}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            position: 'relative',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRadius: '0.55rem',   // same corners as the grid cards on other pages
            overflow: 'hidden',
            backgroundColor: theme.palette.background.alt,
            border: gridBorder,
          }}
        >
          {dragging && hasConversation && (
            <Box
              sx={{
                position: 'absolute',
                inset: 8,
                zIndex: 30,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                borderRadius: 2,
                border: `2px dashed ${accent}`,
                backgroundColor: alpha(theme.palette.background.alt, 0.92),
                pointerEvents: 'none',        // so child elements don't trigger dragleave
              }}
            >
              <AttachFile sx={{ fontSize: 40, color: accent }} />
              <Typography variant="h6">Drop files to attach</Typography>
              <Typography variant="body2" color="text.secondary">Up to {MAX_FILES} files</Typography>
            </Box>
          )}

          <Box
            p={1.5}
            borderBottom={`1px solid ${theme.palette.divider}`}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Box display="flex" alignItems="center" minWidth={0} flex={1}>
              {isMobile && (
                <IconButton size="small" onClick={() => setShowChatOnMobile(false)} aria-label="Back to contacts" sx={{ mr: 0.5 }}>
                  <ArrowBack />
                </IconButton>
              )}

              {selectedGroup ? (
                <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}><Groups /></Avatar>
                  <Box minWidth={0}>
                    <Typography variant="h3" noWrap sx={{ fontSize: { xs: '1rem', md: undefined } }}>{selectedGroup.name}</Typography>
                    <Typography variant="caption" color="text.secondary" onClick={openMembers}
                      sx={{ cursor: 'pointer', '&:hover': { color: accent, textDecoration: 'underline' } }}>
                      {selectedGroup.members.length} members
                    </Typography>
                  </Box>
                </Box>
              ) : selectedUser ? (
                <Box display="flex" alignItems="center" gap={0.2} minWidth={0}>
                  {isBotChat ? (
                    <Avatar sx={{ bgcolor: accent, color: accentText, mr: 1 }}><SmartToy /></Avatar>
                  ) : (
                    <UserAvatar selectedUser={selectedUser} />
                  )}
                  <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                    <Typography variant="h3" noWrap sx={{ fontSize: { xs: '1rem', md: undefined }, minWidth: 0 }}>{selectedUser.name}</Typography>
                    {!isBotChat && [].concat(selectedUser.roles ?? []).map((role) => (
                      <Chip key={role} size="small" variant="outlined" label={role} sx={roleChipSx(role)} />
                    ))}
                    {isMuted && (
                      <Tooltip title="Muted">
                        <NotificationsOff sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography variant="h6">No conversation selected</Typography>
              )}

              {hasConversation && !isBotChat && (
                <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 3, alignSelf: 'stretch', alignItems: 'flex-end', mb: -1.5 }}>
                  {renderChatTabs()}
                </Box>
              )}
            </Box>

            {hasConversation  && (
              <Stack direction="row" alignItems="center" sx={{ gap: 0.5 }}>
                <Tooltip title={selectedGroup ? 'Group video call' : 'Video call'}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={inCall}
                      onClick={() =>
                        selectedGroup
                          ? startGroupCall(selectedGroup, 'video')
                          : startCall(selectedUser, 'video')
                      }
                      sx={{ color: theme.palette.secondary.main, '&.Mui-disabled': { color: theme.palette.text.disabled } }}
                    >
                      <Videocam />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={selectedGroup ? 'Group audio call' : 'Audio call'}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={inCall}
                      onClick={() =>
                        selectedGroup
                          ? startGroupCall(selectedGroup, 'audio')
                          : startCall(selectedUser, 'audio')
                      }
                      sx={{ color: theme.palette.secondary.main, '&.Mui-disabled': { color: theme.palette.text.disabled } }}
                    >
                      <Call />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Start a group chat">
                  <IconButton 
                    size="small" 
                    onClick={() => { openGroupDialog(); }} 
                    sx={{ 
                      display: { xs: 'none', md: 'inline-flex' },
                      color: theme.palette.secondary.main 
                    }}
                  >
                    <GroupAdd />
                  </IconButton>
                </Tooltip>

                {/* Divider after group chat icon */}
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: 24,
                    alignSelf: 'center',
                    mx: 0.5,
                    borderColor: theme.palette.mode === 'dark'
                      ? theme.palette.secondary[500]
                      : theme.palette.neutral[300],
                  }}
                />

                <Tooltip title="Find in chat">
                  <IconButton
                    size="small"
                    color={findOpen ? 'primary' : 'default'}
                    onClick={() => (findOpen ? closeFind() : setFindOpen(true))}
                  >
                    <Search />
                  </IconButton>
                </Tooltip>

                <Tooltip title="More chat options">
                  <IconButton size="small" onClick={(e) => setChatMenuAnchor(e.currentTarget)}>
                    <MoreVert />
                  </IconButton>              
                </Tooltip>
              </Stack>
            )}
          </Box>

          {/* On phones the tabs get their own row under the header */}
          {hasConversation && !isBotChat && isMobile && (
            <Box sx={{ px: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
              {renderChatTabs()}
            </Box>
          )}

          {/* Group call in progress */}
          {selectedGroup && activeGroupCalls[selectedGroupId] && !(call?.kind === 'group' && call.callId === activeGroupCalls[selectedGroupId].callId) && (() => {
            const gc = activeGroupCalls[selectedGroupId]
            const info = { callId: gc.callId, groupId: selectedGroupId, groupName: selectedGroup.name, type: gc.type, mode: gc.mode }
            return (
              <Box
                px={1.5}
                py={0.75}
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: alpha(theme.palette.success.main, 0.12),
                }}
              >
                {gc.type === 'video'
                  ? <Videocam fontSize="small" sx={{ color: theme.palette.success.main }} />
                  : <Call fontSize="small" sx={{ color: theme.palette.success.main }} />}

                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {gc.type === 'video' ? 'Video' : 'Audio'} call in progress ·{' '}
                  {gc.participants.length > 4
                    ? `${gc.participants.length} people`
                    : gc.participants.map((p) => p.name).join(', ')}
                </Typography>

                {gc.type === 'video' && (
                  <Button size="small" disabled={inCall} onClick={() => joinGroupCall(info, false)} sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                    Audio only
                  </Button>
                )}

                <Button size="small" variant="contained" disabled={inCall} onClick={() => joinGroupCall(info)} sx={goldButtonSx}>
                  Join
                </Button>
              </Box>
            )
          })()}          

          <PhotoViewer
            items={chatImages}
            index={viewerImageIndex >= 0 ? viewerImageIndex : null}
            onIndexChange={(i) => setViewerImageId(chatImages[i]?._id ?? null)}
            onClose={() => setViewerImageId(null)}
            onDownload={handleDownloadAttachment}
            onOpen={handleOpenAttachment}
            userById={userById}
            meId={auth?._id?.toString()}
          />

          {chatTab !== 'chat' && hasConversation && (
            <ChatAttachments
              kind={chatTab}
              params={selectedGroupId ? { groupId: selectedGroupId } : { recipientId: selectedUserId }}
              userById={userById}
              meId={auth?._id?.toString()}
              onOpen={handleOpenAttachment}
              onOpenMenu={handleOpenAttachmentMenu}
              onDownload={handleDownloadAttachment}
              onMarkOpened={markAttachmentOpened}
            />
          )}

          {/* Chat tab. Hidden (not unmounted) on Files/Photos so scroll position and draft survive */}
          <Box
            sx={{
              display: chatTab === 'chat' || !hasConversation ? 'flex' : 'none',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
          {/* Pinned message bar */}
          {currentPin && (
            <Box
              px={1.5}
              py={0.5}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(accent, 0.08),
              }}
            >
              <PushPin fontSize="small" sx={{ color: accent, transform: 'rotate(45deg)' }} />

              <Box
                onClick={() => handleJumpToMessage(currentPin._id)}
                sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                  {getSenderName(pinSource)}
                  {livePins.length > 1 ? ` · Pinned ${pinIdx + 1} of ${livePins.length}` : ' · Pinned'}
                </Typography>
                <Typography variant="body2" noWrap color="text.secondary">
                  {getPreviewText(pinSource)}
                </Typography>
              </Box>

              {livePins.length > 1 && (
                <Tooltip title="Next pinned message">
                  <IconButton size="small" onClick={() => setPinIndex((i) => (i + 1) % livePins.length)}>
                    <KeyboardArrowDown fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Unpin">
                <IconButton size="small" onClick={() => handleTogglePin(pinSource)}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Find bar */}
          {findOpen && (
            <Box
              px={1.5}
              py={1}
              display="flex"
              alignItems="center"
              gap={1}
              borderBottom={`1px solid ${theme.palette.divider}`}
            >
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Find in chat"
                value={findQuery}
                onChange={(e) => setFindQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    goToMatch(findIndex + (e.shiftKey ? 1 : -1)); // Enter = older, Shift+Enter = newer
                  } else if (e.key === 'Escape') {
                    closeFind();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', minWidth: 48, textAlign: 'center' }}>
                {findQuery.trim()
                  ? findMatches.length
                    ? `${findMatches.length - findIndex} of ${findMatches.length}`
                    : 'No results'
                  : ''}
              </Typography>

              <IconButton size="small" disabled={!findMatches.length} onClick={() => goToMatch(findIndex - 1)} aria-label="Previous match">
                <KeyboardArrowUp />
              </IconButton>
              <IconButton size="small" disabled={!findMatches.length} onClick={() => goToMatch(findIndex + 1)} aria-label="Next match">
                <KeyboardArrowDown />
              </IconButton>
              <IconButton size="small" onClick={closeFind} aria-label="Close find">
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Box
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            flex={1}
            minHeight={0}
            overflow="auto"
            p={2}
            sx={{
              backgroundColor: theme.palette.background.alt,

              // Transparent / subtle scrollbar
              ...scrollbarSx,
            }}
          >
            {hasConversation ? (
              <Stack spacing={2} ref={contentRef}>
                {!loadingMessages && selectedMessages.length > 0 && (
                  <Box sx={{ height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {loadingOlder ? (
                      <CircularProgress size={18} />
                    ) : hasMore ? (
                      <Button size="small" onClick={() => loadOlderPages()}>Load older messages</Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Beginning of conversation</Typography>
                    )}
                  </Box>
                )}

                {loadingMessages ? (
                  <Box textAlign="center" pt={4}>
                      <CircularProgress />
                  </Box>
                ) : selectedMessages.length === 0 ? (
                    <Box textAlign="center" pt={4}>
                      <Typography color="text.secondary">
                        {isBotChat ? 'Ask the assistant a question to get started.' : 'No messages yet. Send the first message.'}
                      </Typography>
                    </Box>
                  ) : (
                    selectedMessages
                      .map((message) => {
                      const isMine = message.senderId === auth?._id?.toString()

                      // If the message is soft-deleted and it belongs to someone else, hide it completely
                      if (message.deletedAt && !isMine) {
                        return null;
                      }

                       // 1. Compute current calendar date string to watch for unique shifts
                      const currentDateStr = message.createdAt ? new Date(message.createdAt).toDateString() : new Date().toDateString();
                      let showDateDivider = false;

                      if (currentDateStr !== lastDateStr) {
                        showDateDivider = true;
                        lastDateStr = currentDateStr; // Updates structural baseline reference state
                      }

                      const dateDivider = showDateDivider ? (
                        <Box display="flex" alignItems="center" width="100%" my={2}>
                          <Box flex={1} height="1px" bgcolor={theme.palette.divider || '#e0e0e0'} />
                          <Typography
                            variant="caption"
                            sx={{
                              mx: 2,
                              color: theme.palette.text?.secondary || '#666666',
                              fontWeight: 400,
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatDividerDate(message.createdAt || new Date())}
                          </Typography>
                          <Box flex={1} height="1px" bgcolor={theme.palette.divider || '#e0e0e0'} />
                        </Box>
                      ) : null;

                      // Call and group events: a centered line instead of a bubble
                      if (message.type === 'call' || message.type === 'system') {
                        const { Icon, text, error: isError } = getEventInfo(message);

                        return (
                          <Box
                            key={message._id}
                            id={`msg-${message._id}`}
                            width="100%"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={0.5}
                          >
                            {dateDivider}

                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '999px',
                                border: `1px solid ${isError ? alpha(theme.palette.error.main, 0.5) : theme.palette.divider}`,
                                backgroundColor: isError
                                  ? alpha(theme.palette.error.main, 0.1)
                                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                                color: isError ? theme.palette.error.main : theme.palette.text.secondary,
                              }}
                            >
                              <Icon sx={{ fontSize: '1rem' }} />
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {text}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                {formatTime(message.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      }
                      
                      return (
                        <Box 
                          key={message._id} 
                          width="100%" 
                          display="flex" 
                          flexDirection="column" 
                          gap={0.5}
                        >      
                          {dateDivider}

                          <Box 
                            display="flex" 
                            flexDirection={isMine ? 'row-reverse' : 'row'}
                            alignItems="center"
                            gap={0.5}
                            sx={{ flexShrink: 1, position: 'relative', ml: !isMine ? 7 : 0, }}
                          >
                            {!isMine && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    textAlign: isMine ? 'right' : 'left',
                                    color: theme.palette.text?.secondary || '#888888',
                                    opacity: 0.8,
                                    px: 0.5
                                  }}
                                >
                                  {senderOf(message)?.name}
                                </Typography>
                            )}
                            {/* ==================== SUBTITLE BADGE ==================== */}
                            {message.edited && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 500,
                                  textAlign: isMine ? 'right' : 'left',
                                  color: theme.palette.text?.secondary || '#888888',
                                  opacity: 0.8,
                                  px: 0.5,
                                  mr: '1.4rem',
                                }}
                              >
                                Edited
                              </Typography>
                            )}
                            {/* ======================================================================== */}                          
                          
                            {pinnedIdSet.has(message._id) && (
                              <PushPin sx={{ fontSize: '0.85rem', opacity: 0.7, transform: 'rotate(45deg)', mr: '1.4rem' }} />
                            )}
                          </Box>                          
                          
                          <Box
                            id={`msg-${message._id}`}
                            key={message._id || Math.random()}
                            display="flex"
                            // row-reverse puts time on left of my bubbles, row puts time on right of their bubbles
                            flexDirection={isMine ? 'row-reverse' : 'row'}
                            alignItems="flex-start"
                            alignSelf={isMine ? 'flex-end' : 'flex-start'}
                            gap={0.2} // Adds nice spacing between the bubble and the time text
                            maxWidth="80%"
                            width="100%"
                            position="relative"
                            sx={{
                              '&:hover .time-display': { opacity: 1 },
                              borderRadius: 2,
                              transition: 'background-color 0.4s ease',
                              backgroundColor: highlightedId === message._id
                                ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(25,118,210,0.12)')
                                : findMatchSet.has(message._id)
                                ? (message._id === currentMatchId ? 'rgba(255,193,7,0.40)' : 'rgba(255,193,7,0.15)')
                                : 'transparent',
                            }}
                          >                              
                            {!isMine
                              ? (senderOf(message)?.isBot
                                  ? <Avatar sx={{ bgcolor: accent, color: accentText, width: 40, height: 40 }}><SmartToy /></Avatar>
                                  : senderOf(message)
                                    ? <UserAvatar selectedUser={senderOf(message)} />
                                    : <div style={{ width: 40 }} />)
                              : <div></div>}

                            {/* Main Structural Message Panel Container */}
                            <Box 
                              display="flex" 
                              flexDirection="column" 
                              sx={{ flexShrink: 1, position: 'relative' }}
                            >

                              {/* ==================== CONDITION: EDITING STATE VS BUBBLE STATE ==================== */}
                              <Box 
                                display="flex" 
                                flexDirection={isMine ? 'row-reverse' : 'row'} 
                                alignItems="center"
                                gap={0.5}
                                width="100%"
                              >
                                {message.deletedAt ? (
                                  // Teams Soft-Deleted Message View Placeholder Box
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    p={1.5}
                                    bgcolor={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}
                                    border={`1px dashed ${theme.palette.divider || '#e0e0e0'}`}
                                    borderRadius={2}
                                    sx={{ flexShrink: 1, minWidth: '240px' }}
                                  >
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontStyle: 'italic', 
                                        color: theme.palette.text.disabled || '#999999' 
                                      }}
                                    >
                                      This message has been deleted.
                                    </Typography>
                                    
                                    {/* Inline Undo Button Trigger Link */}
                                    <Typography
                                      variant="caption"
                                      onClick={() => handleActionClick('delete', message)} // Fires identical endpoint toggle path
                                      sx={{
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        marginLeft: 'auto',
                                        userSelect: 'none',
                                        '&:hover': {
                                          color: theme.palette.primary.light
                                        }
                                      }}
                                    >
                                      Undo
                                    </Typography>
                                  </Box>
                                ) : editingMessageId === message._id ? (
                                  // Inline Text Editor View Box Panel
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={1}
                                    width="100%"
                                    p={1.5}
                                    bgcolor={theme.palette.background.default}
                                    border={`1px solid ${theme.palette.divider || '#e0e0e0'}`}
                                    borderRadius={2}
                                    sx={{ flexShrink: 1, minWidth: { xs: '200px', sm: '300px' } }}
                                  >                                                                  
                                    <TextField
                                      fullWidth
                                      multiline
                                      maxRows={4}
                                      size="small"
                                      value={editDraft}
                                      disabled={updatingMessage}
                                      onChange={(e) => setEditDraft(e.target.value)}
                                      onKeyDown={(e) => {
                                        // Submit on Enter (without holding Shift key)
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          handleSaveEdit(message._id);
                                        }
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          fontSize: '0.875rem',
                                          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary[500] : '#ffffff'
                                        }
                                      }}
                                    />
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                      {/* Cancel Icon Button */}
                                      <IconButton 
                                        size="small" 
                                        onClick={handleCancelEdit}
                                        disabled={updatingMessage}
                                        title="Cancel"
                                        sx={{ 
                                          color: theme.palette.text.secondary,
                                          border: `1px solid ${theme.palette.divider || '#e0e0e0'}`,
                                          borderRadius: 1,
                                          p: 0.5,
                                          '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                                          }
                                        }}
                                      >
                                        <Close sx={{ fontSize: '1.1rem' }} />
                                      </IconButton>

                                      {/* Confirm / Save Icon Button */}
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleSaveEdit(message._id)}
                                        disabled={!editDraft.trim() || updatingMessage}
                                        title="Confirm"
                                        sx={{ 
                                          backgroundColor: theme.palette.primary.main,
                                          color: theme.palette.primary.contrastText,
                                          borderRadius: 1,
                                          p: 0.5,
                                          '&:hover': {
                                            backgroundColor: theme.palette.primary.light
                                          },
                                          '&.Mui-disabled': {
                                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                            color: theme.palette.text.disabled
                                          }
                                        }}
                                      >
                                        {updatingMessage ? (
                                          <CircularProgress size={18} color="inherit" />
                                        ) : (
                                          <Check sx={{ fontSize: '1.1rem' }} />
                                        )}
                                      </IconButton>
                                    </Stack>
                                  </Box>
                                ) : (
                                  // Regular Default Chat Bubble Box Panel
                                  <>
                                    {isMine && (
                                      <>
                                        {/* Condition A: 1:1 chat only - This is the newest message they have read */}
                                        {message._id === receiptIds.lastReadId && (
                                          <CheckCircle 
                                            sx={{ 
                                              fontSize: '0.85rem', 
                                              color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
                                              opacity: 0.9,
                                              mx: 0.25,
                                              mt: '1.5rem',
                                            }} 
                                            titleAccess={getReadTitle(message)}
                                          />
                                        )}

                                        {/* Condition B: Both 1:1 and Group chats - This is your absolute last message and it's sent */}
                                        {message._id === receiptIds.lastSentId &&
                                          (!selectedGroupId ? !message.read : getGroupReaders(message).length === 0) && (
                                          <CheckCircleOutlined
                                            sx={{ 
                                              fontSize: '0.85rem', 
                                              color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
                                              opacity: 0.6,
                                              mx: 0.25,
                                              mt: '1.5rem',
                                            }} 
                                            titleAccess={selectedGroupId ? 'Sent to group' : 'Sent'}
                                          />
                                        )}
                                      </>
                                    )}
                                    <Box
                                      className="bubble-wrapper"
                                      sx={{
                                        width: "fit-content",
                                        "&:hover .toolbox-container": { opacity: 1, visibility: "visible", },                                        
                                      }}
                                    >
                                      <Box
                                        bgcolor={
                                          isOnlyEmojis(message.content)
                                            ? 'transparent'
                                            : isMine
                                            ? myBubbleBg
                                            : (isDark ? theme.palette.primary[400] : theme.palette.background.alt)
                                        }
                                        color={
                                          isOnlyEmojis(message.content)
                                            ? theme.palette.text.primary
                                            : isMine
                                            ? myBubbleText
                                            : theme.palette.text.primary
                                        }
                                        border={
                                          isOnlyEmojis(message.content)
                                            ? 'none'
                                            : isMine
                                            ? `1px solid ${alpha(myBubbleBg, 0.9)}`
                                            : `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : theme.palette.divider}`
                                        }
                                        borderRadius={2}
                                        p={isOnlyEmojis(message.content) ? 0 : 1.5}
                                        boxShadow={isOnlyEmojis(message.content) ? 0 : 1}
                                        sx={{ 
                                          flexShrink: 1, 
                                          cursor: 'pointer', 
                                          // Mine is solid gold (the accent, like the read mark), theirs is a slate tint of the theme; the squared corner points at the sender
                                          ...(!isOnlyEmojis(message.content) && (isMine
                                            ? { borderBottomRightRadius: 4 }
                                            : { borderBottomLeftRadius: 4 })),
                                          mr: (isMine && (message._id !== receiptIds.lastReadId && message._id !== receiptIds.lastSentId)) ? '1.3rem' : '0rem' 
                                        }}
                                        onClick={() => setTappedMessageId((id) => (id === message._id ? null : message._id))}
                                      >
                                        {message.forwarded && (
                                          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5} sx={{ opacity: 0.7 }}>
                                            <ForwardIcon sx={{ fontSize: '0.85rem' }} />
                                            <Typography variant="caption" sx={{ fontStyle: 'italic' }}>Forwarded</Typography>
                                          </Stack>
                                        )}

                                        {(() => {
                                          const replied = getRepliedMessage(message);
                                          if (!message.replyTo) return null;
                                          return (
                                            <Box
                                              onClick={(e) => { e.stopPropagation(); replied && handleJumpToMessage(replied._id); }}
                                              sx={{
                                                mb: 1, px: 1, py: 0.5,
                                                // on my gold bubble the quote uses the bubble's own text color; on theirs, the accent
                                                borderLeft: `3px solid ${isMine ? myBubbleText : isDark ? theme.palette.secondary.main : theme.palette.primary.main}`,
                                                borderRadius: 1,
                                                backgroundColor: isMine
                                                  ? alpha(myBubbleText, 0.14)
                                                  : isDark ? 'rgba(255,255,255,0.08)' : alpha(accent, 0.1),
                                                cursor: replied ? 'pointer' : 'default',
                                                maxWidth: 360,
                                              }}
                                            >
                                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                                {getSenderName(replied)}
                                              </Typography>
                                              <Typography variant="body2" noWrap sx={{ opacity: 0.8, fontStyle: replied?.deletedAt ? 'italic' : 'normal' }}>
                                                {getPreviewText(replied)}
                                              </Typography>
                                            </Box>
                                          );
                                        })()}
                                        <MessageContent
                                          message={message}
                                          handleOpenAttachmentMenu={handleOpenAttachmentMenu}
                                          onOpenImage={(m) => setViewerImageId(m._id)}
                                          onOpenFile={handleOpenAttachment}
                                          isNew={isNewMessageAttachment(message)}
                                          isMine={isMine}
                                        />
                                      </Box>    

                                      {message.reactions?.length > 0 && (
                                        <Stack
                                          direction="row"
                                          flexWrap="wrap"
                                          gap={0.5}
                                          mt={0.5}
                                          justifyContent={isMine ? 'flex-end' : 'flex-start'}
                                        >
                                          {message.reactions.map((r) => {
                                            const meId = auth?._id?.toString()
                                            const mine = r.userIds.includes(meId)
                                            const names = r.userIds
                                              .map((id) => (id === meId ? 'You' : (userById[id]?.name || 'Someone')))
                                              .join(', ')

                                            return (
                                              <Tooltip key={r.emoji} title={names}>
                                                <Box
                                                  onClick={() => handleToggleReaction(message._id, r.emoji)}
                                                  sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    px: 0.9,
                                                    py: 0.15,
                                                    borderRadius: '999px',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontSize: '0.85rem',
                                                    border: `1px solid ${mine ? alpha(accent, 0.7) : theme.palette.divider}`,
                                                    backgroundColor: mine
                                                      ? alpha(accent, 0.15)
                                                      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                                                    '&:hover': { backgroundColor: alpha(accent, 0.22) },
                                                  }}
                                                >
                                                  <span>{r.emoji}</span>
                                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                                    {r.userIds.length}
                                                  </Typography>
                                                </Box>
                                              </Tooltip>
                                            )
                                          })}
                                        </Stack>
                                      )}

                                      {/* ==================== TOP-FLOATING TOOLBOX ==================== */}
                                      {/* Teams style: quick reactions are clickable right in the hover bar */}
                                      <Box
                                        className="toolbox-container"
                                        display="flex"
                                        alignItems="center"
                                        gap={0.25}
                                        px={0.5}
                                        py={0.25}
                                        bgcolor={theme.palette.background.alt}
                                        border={`1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`}
                                        borderRadius={2}
                                        boxShadow={4}
                                        sx={{
                                          position: 'absolute',
                                          top: '-38px', // Sits just above the top edge of the bubble
                                          // Grow into the open side so the bar never runs off the chat
                                          ...(isMine ? { right: '22px' } : { left: '0px' }),
                                          zIndex: 10,
                                          whiteSpace: 'nowrap',
                                          opacity: tappedMessageId === message._id ? 1 : 0,
                                          visibility: tappedMessageId === message._id ? 'visible' : 'hidden',
                                          ...( (editingMessageId !== message._id && !message.deletedAt) && {
                                            transition: 'opacity 0.1s ease-in-out, visibility 0.1s',
                                          }),
                                        }}
                                      >
                                        {QUICK_REACTIONS.map((emoji) => {
                                          const used = message.reactions?.some(
                                            (r) => r.emoji === emoji && r.userIds.includes(auth?._id?.toString())
                                          )

                                          return (
                                            <IconButton
                                              key={emoji}
                                              size="small"
                                              aria-label={`React ${emoji}`}
                                              onClick={() => {
                                                handleToggleReaction(message._id, emoji)
                                                setTappedMessageId(null)
                                              }}
                                              sx={{
                                                width: 30,
                                                height: 30,
                                                fontSize: '1.15rem',
                                                lineHeight: 1,
                                                backgroundColor: used ? alpha(accent, 0.2) : 'transparent',
                                                transition: 'transform 0.12s ease',
                                                '&:hover': { transform: 'scale(1.25)', backgroundColor: alpha(accent, 0.12) },
                                              }}
                                            >
                                              {emoji}
                                            </IconButton>
                                          )
                                        })}

                                        <Tooltip title="More reactions">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              setReactionAnchor(e.currentTarget)
                                              setReactionMessageId(message._id)
                                            }}
                                            sx={toolboxIconSx}
                                          >
                                            <AddReaction sx={{ fontSize: '1.05rem' }} />
                                          </IconButton>
                                        </Tooltip>

                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

                                        {isMine ? (
                                          <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleActionClick('edit', message)} sx={toolboxIconSx}>
                                              <Edit sx={{ fontSize: '1.05rem' }} />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip title="Reply">
                                            <IconButton size="small" onClick={() => handleActionClick('reply', message)} sx={toolboxIconSx}>
                                              <Reply sx={{ fontSize: '1.05rem' }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}

                                        <Tooltip title="More options">
                                          <IconButton size="small" onClick={(e) => handleOpenMenu(e, message)} sx={toolboxIconSx}>
                                            <MoreHoriz sx={{ fontSize: '1.05rem' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>                               
                                  </>
                                )}
                              </Box>
                            </Box>
                            {/* ================================================================================== */}

                            {/* The Timestamp Container - Hidden by default, reveals on parent hover */}
                            <Typography 
                              className="time-display" // Custom identifier class linked to parent hover style trigger
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.grey[400] || '#888888', 
                                fontSize: '0.7rem',
                                whiteSpace: 'nowrap', // Prevents the time text from breaking onto two lines
                                flexShrink: 0, // Keeps the time text from getting squished
                                opacity: tappedMessageId === message._id ? 1 : 0,
                                transition: 'opacity 0.2s ease-in-out',
                                mt: 1.5,
                              }}
                            >
                              {formatTime(message.edited ? message.updatedAt : message.createdAt)}
                            </Typography>

                            
                          </Box>
                        </Box>
                      )
                    })
                  )
                }
              </Stack>
            ) : (
              <Box p={4} textAlign="center">
                <Typography variant="body1" color="text.secondary">
                  Select a conversation to begin chatting.
                </Typography>
              </Box>
            )}
          </Box>

          <Divider />

          <Box sx={{ px: 2, height: 22, display: 'flex', alignItems: 'center' }}>
            {typingText && (
              <Typography
                variant="caption"
                sx={{ fontStyle: 'italic', color: theme.palette.text.secondary, display: 'flex', alignItems: 'center' }}
              >
                {typingText}
                <TypingDots />
              </Typography>
            )}
          </Box>

          {replyingTo && (
            <Box px={2} pt={1.5}>
              <Box
                sx={{
                  px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1,
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: 1,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                }}
              >
                <Reply fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Replying to {getSenderName(replyingTo)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {getPreviewText(replyingTo)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setReplyingTo(null)} aria-label="Cancel reply">
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>            
          )}

          <Box p={2}>
            {error && (
              <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                <Typography variant="caption" color="error" sx={{ flex: 1 }}>
                  {error}
                </Typography>
                <IconButton size="small" onClick={() => setError(null)} aria-label="Dismiss error">
                  <Close color="error" sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            )}

            {selectedFiles.length > 0 && (
              <Box mb={1.25}>
                {sending && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box flex={1}>
                      <LinearProgress variant="determinate" value={overallPercent} sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                    <Typography variant="caption" sx={{ minWidth: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {overallPercent}%
                    </Typography>
                    <Button size="small" onClick={() => uploadAbortRef.current?.abort()} sx={{ minWidth: 0 }}>
                      Cancel
                    </Button>
                  </Box>
                )}

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {selectedFiles.map((file, index) => (
                    <SelectedFileChip
                      key={fileKey(file)}
                      file={file}
                      disabled={sending}
                      progress={sending ? (fileProgress[fileKey(file)] ?? 0) : undefined}
                      onRemove={() => handleRemoveFile(index)}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <Box sx={{ position: 'relative' }}>
              {mentionState && mentionCandidates.length > 0 && (
                <Paper
                  elevation={6}
                  sx={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    right: 0,
                    mb: 0.5,
                    zIndex: 20,
                    maxHeight: 240,
                    overflow: 'auto',
                    backgroundColor: theme.palette.background.alt,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <List dense disablePadding>
                    {mentionCandidates.map((u, i) => (
                      <ListItemButton
                        key={u._id}
                        selected={i === mentionIndex}
                        onMouseDown={(e) => {
                          e.preventDefault()        // keep focus in the input
                          insertMention(u)
                        }}
                        onMouseEnter={() => setMentionIndex(i)}
                      >
                        <UserAvatar selectedUser={u} />
                        <ListItemText
                          sx={{ ml: 1 }}
                          primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>}
                          secondary={u.email ? <Typography variant="caption" color="text.secondary" noWrap>{u.email}</Typography> : null}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              )}    

              <TextField
                fullWidth
                size="small"
                inputRef={chatInputRef}
                placeholder={
                  selectedGroup ? `Message ${selectedGroup.name}`
                  : isBotChat ? 'Ask the assistant…'
                  : selectedUser ? `Message ${selectedUser.name}`
                  : 'Select a contact first'
                }
                value={draft}
                onChange={handleDraftChange}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  if (mentionState && mentionCandidates.length > 0) {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setMentionIndex((i) => (i + 1) % mentionCandidates.length)
                      return
                    }
                    if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length)
                      return
                    }
                    if (e.key === 'Enter' || e.key === 'Tab') {
                      e.preventDefault()
                      insertMention(mentionCandidates[mentionIndex])
                      return
                    }
                  }

                  if (e.key === 'Escape' && mentionState) {
                    setMentionState(null)
                    return
                  }

                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>

                    <IconButton
                      aria-label="Attach files"
                      sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": { color: accent, backgroundColor: alpha(accent, 0.12) },
                      }}
                      disabled={!hasConversation}
                      onClick={() => fileInputRef.current.click()}
                    >
                      <AttachFile />
                    </IconButton>

                    <Tooltip title="Send files to people and groups">
                      <IconButton
                        aria-label="Send files"
                        sx={{
                          color: theme.palette.text.secondary,
                          "&:hover": { color: accent, backgroundColor: alpha(accent, 0.12) },
                        }}
                        onClick={() => setSendFilesOpen(true)}
                      >
                        <DriveFolderUpload />
                      </IconButton>
                    </Tooltip>

                    <ChatEmojiPicker
                      onSelectEmoji={handleSelectEmoji}
                      disabled={!hasConversation}
                      appTheme={theme}
                    />

                    <Divider 
                      orientation="vertical" 
                      flexItem 
                      sx={{ 
                        height: 24, 
                        alignSelf: 'center', 
                        borderColor: theme.palette.divider,
                        mx: 0.5  
                      }} 
                    />

                    {/* Send Button */}
                      <IconButton
                        aria-label="Send"
                        sx={{
                          // gold when there is something to send
                          color: accent,
                          "&:hover": { backgroundColor: alpha(accent, 0.14) },
                          "&.Mui-disabled": { color: theme.palette.text.disabled },
                        }}
                        onClick={handleSend}  
                        disabled={
                          !hasConversation ||
                          (!draft.trim() && selectedFiles.length === 0) ||
                          sending
                        }
                      >
                        {sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={!hasConversation}
                multiline
                minRows={1}
                maxRows={3}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    color: theme.palette.text.primary,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
                    "& fieldset": { borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' },
                    "&:hover fieldset": { borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)' },
                    "&.Mui-focused fieldset": { borderColor: accent, borderWidth: 1 },
                  },
                  "& .MuiInputBase-input::placeholder": { color: theme.palette.text.secondary, opacity: 0.9 },
                }}
              />
            </Box>
          </Box>
          </Box>
        </Paper>
      </Box>

      {/* Standalone More Options Floating Dropdown Overlay Menu element */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        elevation={3}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: { 
              minWidth: 130, 
              borderRadius: 1.5, 
              p: 0.5,
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary[700] : theme.palette.background.default,
              border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.primary[500] : theme.palette.neutral.main}`
            }
          }
        }}
      >
        {(() => {
          // Compute ownership for the specific message currently assigned to the open menu
          const menuMessageIsMine = activeMenuMessage?.senderId === auth?._id?.toString();

          const disabled = Boolean(activeMenuMessage?.deletedAt);

          return (
            <>
              <MenuItem disabled={disabled} onClick={() => handleActionClick('reply', activeMenuMessage)} sx={{ gap: 1.5, py: 1, borderRadius: 1 }}>
                <Reply fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                <Typography variant="body2" color="text.primary">Reply</Typography>
              </MenuItem>

              <MenuItem disabled={disabled} onClick={() => handleActionClick('forward', activeMenuMessage)} sx={{ gap: 1.5, py: 1, borderRadius: 1 }}>
                <Redo fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                <Typography variant="body2" color="text.primary">Forward</Typography>
              </MenuItem>

              <MenuItem disabled={disabled} onClick={() => handleActionClick('pin', activeMenuMessage)} sx={{ gap: 1.5, py: 1, borderRadius: 1 }}>
                {pinnedIdSet.has(activeMenuMessage?._id)
                  ? <PushPinOutlined fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                  : <PushPin fontSize="small" sx={{ color: theme.palette.text.secondary }} />}
                <Typography variant="body2" color="text.primary">
                  {pinnedIdSet.has(activeMenuMessage?._id) ? 'Unpin' : 'Pin'}
                </Typography>
              </MenuItem>

              {menuMessageIsMine && (
                <MenuItem onClick={() => handleActionClick('delete', activeMenuMessage)} sx={{ gap: 1.5, py: 1, borderRadius: 1, color: theme.palette.error.main }}>
                  <Delete fontSize="small" color="error" />
                  <Typography variant="body2">Delete</Typography>
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      <Menu
        anchorEl={attachmentMenuAnchor}
        open={Boolean(attachmentMenuAnchor)}
        onClose={handleCloseAttachmentMenu}
        anchorOrigin={{ vertical:'bottom', horizontal:'right' }}
        transformOrigin={{ vertical:'top', horizontal:'right' }}
      >
        <MenuItem
          onClick={()=>{
            handleOpenAttachment(attachmentMenuMessage);
            handleCloseAttachmentMenu();
          }}
        >
          <Search fontSize="small" sx={{mr:1}}/>
          Open in Browser
        </MenuItem>


        <MenuItem
          onClick={()=>{
            handleDownloadAttachment(attachmentMenuMessage);
            handleCloseAttachmentMenu();
          }}
        >
          <AttachFile fontSize="small" sx={{mr:1}}/>
          Download
        </MenuItem>


        <MenuItem
          onClick={async ()=>{
            await handleCopyAttachmentLink(attachmentMenuMessage);
            handleCloseAttachmentMenu();
          }}
        >
          <Check fontSize="small" sx={{mr:1}}/>
          Copy link
        </MenuItem>

      </Menu>

      <Menu
        anchorEl={chatMenuAnchor}
        open={Boolean(chatMenuAnchor)}
        onClose={() => setChatMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: chatMenuPaperSx } }}
      >
        {/* Which chat these options are for */}
        <Box display="flex" alignItems="center" gap={1.25} px={2} pt={1.25} pb={1}>
          {selectedGroup ? (
            <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}><Groups fontSize="small" /></Avatar>
          ) : isBotChat ? (
            <Avatar sx={{ bgcolor: accent, color: accentText, width: 36, height: 36 }}><SmartToy fontSize="small" /></Avatar>
          ) : (
            <Avatar
              src={avatarSrc(selectedUser?.avatar)}
              sx={{ width: 36, height: 36, ...avatarColor(selectedUser?.name, theme.palette.mode) }}
            >
              {getInitials(selectedUser?.name)}
            </Avatar>
          )}
          <Box minWidth={0}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {selectedGroup?.name ?? selectedUser?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {selectedGroup
                ? `Group · ${selectedGroup.members.length} members`
                : isBotChat
                ? 'Assistant'
                : [].concat(selectedUser?.roles ?? []).join(', ') || 'Chat'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: '0 !important' }} />
        <Typography sx={chatMenuSectionSx}>Chat</Typography>

        <MenuItem onClick={handleMarkUnread}>
          <ListItemIcon><MarkChatUnread fontSize="small" /></ListItemIcon>
          <ListItemText>Mark as unread</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleToggleMute}>
          <ListItemIcon>
            {isMuted ? <Notifications fontSize="small" /> : <NotificationsOff fontSize="small" />}
          </ListItemIcon>
          <ListItemText secondary={isMuted ? 'You won’t get notifications' : null}>
            {isMuted ? 'Unmute' : 'Mute'}
          </ListItemText>
        </MenuItem>

        {selectedGroup && [
          <Divider key="g-div" sx={{ my: '4px !important' }} />,
          <Typography key="g-title" sx={chatMenuSectionSx}>Group</Typography>,
          <MenuItem key="members" onClick={() => { setChatMenuAnchor(null); openMembers(); }}>
            <ListItemIcon><Groups fontSize="small" /></ListItemIcon>
            <ListItemText>Group members</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {selectedGroup.members.length}
            </Typography>
          </MenuItem>,
          <MenuItem key="recordings" onClick={() => { setChatMenuAnchor(null); setRecordingsOpen(true); }}>
            <ListItemIcon><GraphicEq fontSize="small" /></ListItemIcon>
            <ListItemText>Meeting recordings</ListItemText>
          </MenuItem>,

          <Divider key="m-div" sx={{ my: '4px !important' }} />,
          <Typography key="m-title" sx={chatMenuSectionSx}>Private meeting</Typography>,
          <MenuItem key="pvideo" disabled={inCall} onClick={() => { setChatMenuAnchor(null); startGroupCall(selectedGroup, 'video', { private: true }); }}>
            <ListItemIcon><Videocam fontSize="small" /></ListItemIcon>
            <ListItemText>Start video meeting</ListItemText>
          </MenuItem>,
          <MenuItem key="paudio" disabled={inCall} onClick={() => { setChatMenuAnchor(null); startGroupCall(selectedGroup, 'audio', { private: true }); }}>
            <ListItemIcon><Call fontSize="small" /></ListItemIcon>
            <ListItemText>Start audio meeting</ListItemText>
          </MenuItem>,
        ]}

        <Divider sx={{ my: '4px !important' }} />

        {selectedGroup && (
          <MenuItem
            onClick={() => { setChatMenuAnchor(null); setLeaveGroupOpen(true); }}
            sx={chatMenuDangerSx}
          >
            <ListItemIcon><ExitToApp fontSize="small" /></ListItemIcon>
            <ListItemText>Leave group</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => { setChatMenuAnchor(null); setDeleteChatOpen(true); }}
          sx={chatMenuDangerSx}
        >
          <ListItemIcon><DeleteOutline fontSize="small" /></ListItemIcon>
          <ListItemText>Delete chat</ListItemText>
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(reactionAnchor)}
        anchorEl={reactionAnchor}
        onClose={() => setReactionAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.alt,
          },
        }}
      >
        {/* The quick six are already in the hover bar; this is the full set */}
        <EmojiPicker
          theme={isDark ? EmojiTheme.DARK : EmojiTheme.LIGHT}
          lazyLoadEmojis
          previewConfig={{ showPreview: false }}
          height={380}
          width={320}
          onEmojiClick={(emojiData) => {
            handleToggleReaction(reactionMessageId, emojiData.emoji)
            setReactionAnchor(null)
            setTappedMessageId(null)
          }}
        />
      </Popover>

      {/* ==================== DELETE DIALOG ==================== */}
      <ConfirmDialog
        open={deleteChatOpen}
        onClose={() => setDeleteChatOpen(false)}
        onConfirm={handleDeleteChat}
        busy={deletingChat}
        title="Delete chat?"
        message="This removes the conversation history for you only. They will still see their copy, and new messages will appear as usual."
        item={selectedGroup?.name ?? selectedUser?.name}
        itemDetail={selectedGroup ? 'Group chat' : 'Direct chat'}
        itemIcon={selectedGroup ? Groups : ChatBubbleOutline}
        confirmLabel="Delete chat"
      />

      {/* ==================== SEND FILES DIALOG ==================== */}
      <SendFilesDialog
        open={sendFilesOpen}
        onClose={() => setSendFilesOpen(false)}
        users={fileRecipients}
        groups={Array.isArray(groups) ? groups : []}
        defaultTargets={convId && !isBotChat ? [convId] : []}
        onSent={handleFilesSent}
      />

      {/* ==================== FORWARD DIALOG ==================== */}
      <Dialog
        open={Boolean(forwardMessage)}
        onClose={closeForwardDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.text.primary,
            borderRadius: 2,
            border: `1px solid ${
              theme.palette.mode === 'dark'
                ? theme.palette.primary[500]
                : theme.palette.neutral[200]
            }`,
            boxShadow: 8,
            overflow: 'hidden',
          },
        }}
      >
        {/* ==================== FORWARD DIALOG TITLE ==================== */}
        <DialogTitle
          sx={{
            px: 2,
            py: 1.5,
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.secondary[100]
                : theme.palette.neutral[900],
            fontWeight: 600,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          Forward message
        </DialogTitle>

        {/* ==================== FORWARD DIALOG CONTENT ==================== */}
        <DialogContent
          dividers
          sx={{
            px: 2,
            py: 1.75,
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background.alt,
          }}
        >
          {/* Original message preview */}
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 1,
              borderLeft: `3px solid ${theme.palette.primary.main}`,
              borderRadius: 1,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(0,0,0,0.04)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 0.25,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              Message to forward
            </Typography>

            <Typography
              variant="body2"
              noWrap
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              {getPreviewText(forwardMessage)}
            </Typography>
          </Box>

          {/* Search contacts */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts"
            value={forwardQuery}
            onChange={(e) => setForwardQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.secondary[300]
                          : theme.palette.neutral[500],
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                color: theme.palette.text.primary,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary[700]
                    : theme.palette.background.default,

                '& fieldset': {
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary[500]
                      : theme.palette.neutral[300],
                },

                '&:hover fieldset': {
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary[300]
                      : theme.palette.neutral[400],
                },

                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },

              '& .MuiInputBase-input::placeholder': {
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.secondary[400]
                    : theme.palette.neutral[400],
                opacity: 1,
              },
            }}
          />

          {/* Contact list */}
          <List
            dense
            sx={{
              maxHeight: 240,
              overflow: 'auto',
              my: 1,
              py: 0.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.primary[700]
                  : theme.palette.background.default,

              ...scrollbarSx,
            }}
          >
            {forwardableUsers.length === 0 && (
              <Typography
                color="text.secondary"
                sx={{
                  p: 1.5,
                  textAlign: 'center',
                }}
              >
                No contacts found.
              </Typography>
            )}

            {forwardableUsers.map((u) => {
              const selected = forwardTargets.includes(u._id);

              return (
                <ListItem
                  key={u._id}
                  disablePadding
                  sx={{
                    px: 0.5,
                  }}
                >
                  <ListItemButton
                    onClick={() => toggleForwardTarget(u._id)}
                    selected={selected}
                    sx={{
                      borderRadius: 1,

                      '&.Mui-selected': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(25,118,210,0.08)',
                      },

                      '&.Mui-selected:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.12)'
                            : 'rgba(25,118,210,0.12)',
                      },

                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={selected}
                        tabIndex={-1}
                        disableRipple
                        sx={{
                          color:
                            theme.palette.mode === 'dark'
                              ? theme.palette.secondary[400]
                              : theme.palette.neutral[500],

                          '&.Mui-checked': {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    </ListItemIcon>

                    <UserAvatar selectedUser={u} />

                    <ListItemText
                      sx={{
                        ml: 1,
                      }}
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {u.name}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {u.email || 'No email available'}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          {/* Forward note */}
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={3}
            placeholder="Add a message (optional)"
            value={forwardNote}
            onChange={(e) => setForwardNote(e.target.value)}
            sx={{
              mt: 1,

              '& .MuiOutlinedInput-root': {
                color: theme.palette.text.primary,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary[700]
                    : theme.palette.background.default,

                '& fieldset': {
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary[500]
                      : theme.palette.neutral[300],
                },

                '&:hover fieldset': {
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary[300]
                      : theme.palette.neutral[400],
                },

                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },

              '& .MuiInputBase-input::placeholder': {
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.secondary[400]
                    : theme.palette.neutral[400],
                opacity: 1,
              },
            }}
          />
        </DialogContent>

        {/* ==================== FORWARD DIALOG ACTIONS ==================== */}
        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.primary[700]
                : theme.palette.background.default,
          }}
        >
          <Button
            onClick={closeForwardDialog}
            disabled={forwarding}
            sx={{
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.secondary[200]
                  : theme.palette.neutral[700],

              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleForward}
            disabled={forwarding || forwardTargets.length === 0}
            startIcon={
              forwarding ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ForwardIcon />
              )
            }
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: 1.5,
              px: 2,

              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },

              '&.Mui-disabled': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.10)'
                    : 'rgba(0,0,0,0.10)',
                color: theme.palette.text.disabled,
              },
            }}
          >
            Forward
            {forwardTargets.length > 0
              ? ` (${forwardTargets.length})`
              : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== GROUP DIALOG ==================== */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => !creatingGroup && setGroupDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.5,
            fontWeight: 600,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          Start a group chat
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2,
            py: 1.75,
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            inputProps={{ maxLength: 60 }}
            sx={{ mt: 1, mb: 2, ...dialogFieldSx }}
          />

          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts"
            value={groupQuery}
            onChange={(e) => setGroupQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={dialogFieldSx}
          />

          {/* Hint + counter pill */}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mt={1.5}>
            <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
              <InfoOutlined sx={{ fontSize: '1.1rem', color: theme.palette.text.secondary }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                Pick at least 1 contact.
              </Typography>
            </Box>

            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderRadius: '999px',
                fontSize: '0.75rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                backgroundColor: groupMembers.length >= 1
                  ? alpha(theme.palette.success.main, 0.2)
                  : alpha('#ffffff', 0.08),
                color: groupMembers.length >= 1
                  ? theme.palette.success.light
                  : theme.palette.text.secondary,
              }}
            >
              {groupMembers.length + 1} / 2+ members
            </Box>
          </Box>

          <List dense sx={dialogListSx}>
            {groupCandidates.length === 0 && (
              <Typography color="text.secondary" sx={{ p: 1.5, textAlign: 'center' }}>
                No contacts found.
              </Typography>
            )}

            {groupCandidates.map((u) => {
              const selected = groupMembers.includes(u._id);

              return (
                <ListItem key={u._id} disablePadding sx={{ px: 0.5, py: 0.25 }}>
                  <ListItemButton
                    onClick={() => toggleGroupMember(u._id)}
                    selected={selected}
                    sx={{
                      borderRadius: 1,
                      border: '1px solid transparent',
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: alpha(accent, 0.14),
                        borderColor: alpha(accent, 0.6),
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: alpha(accent, 0.2),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        checked={selected}
                        tabIndex={-1}
                        disableRipple
                        sx={{
                          color: theme.palette.text.secondary,
                          '&.Mui-checked': { color: accent },
                        }}
                      />
                    </ListItemIcon>

                    <UserAvatar selectedUser={u} />

                    <ListItemText
                      sx={{ ml: 1 }}
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {u.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary }}>
                          {u.email || 'No email available'}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Button
            onClick={() => setGroupDialogOpen(false)}
            disabled={creatingGroup}
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={!groupName.trim() || groupMembers.length < 1 || creatingGroup}
            startIcon={creatingGroup ? <CircularProgress size={16} color="inherit" /> : <GroupAdd />}
            sx={{
              backgroundColor: accent,
              color: accentText,
              fontWeight: 700,
              borderRadius: 1.5,
              px: 2.5,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.secondary.light,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
                color: theme.palette.text.disabled,
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== LEAVE GROUP DIALOG ==================== */}
      <ConfirmDialog
        open={leaveGroupOpen}
        onClose={() => setLeaveGroupOpen(false)}
        onConfirm={handleLeaveGroup}
        busy={leavingGroup}
        title="Leave group?"
        message="You will stop receiving its messages and it will be removed from your chat list. The other members will still see the group."
        item={selectedGroup?.name}
        itemIcon={Groups}
        note="A member can add you back later."
        confirmLabel="Leave group"
        busyLabel="Leaving…"
        confirmIcon={<ExitToApp />}
      />

      {/* ==================== GROUP MEMBERS DIALOG ==================== */}
      <Dialog
        open={membersOpen && Boolean(selectedGroup)}
        onClose={() => !membersBusy && setMembersOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.5,
            fontWeight: 600,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          Group members
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.75, backgroundColor: theme.palette.background.alt }}>
          {membersError && (
            <Typography variant="caption" color="error" display="block" mt={1}>
              {membersError}
            </Typography>
          )}

          {/* Rename */}
          <Box display="flex" gap={1} mt={1.5} mb={2}>
            <TextField
              fullWidth
              size="small"
              label="Group name"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRenameGroup();
                }
              }}
              inputProps={{ maxLength: 60 }}
              sx={dialogFieldSx}
            />
            <Button
              variant="contained"
              onClick={handleRenameGroup}
              disabled={
                membersBusy ||
                !renameDraft.trim() ||
                renameDraft.trim() === selectedGroup?.name
              }
              sx={goldButtonSx}
            >
              Save
            </Button>
          </Box>

          {/* Header row */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {selectedGroup?.members.length} members
            </Typography>

            <Button
              size="small"
              startIcon={addMode ? <Close /> : <PersonAdd />}
              onClick={() => {
                setAddMode((v) => !v);
                setAddTargets([]);
                setAddQuery('');
                setMembersError('');
              }}
              disabled={membersBusy}
              sx={{ color: accent, fontWeight: 700 }}
            >
              {addMode ? 'Cancel' : 'Add people'}
            </Button>
          </Box>

          {addMode ? (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Search contacts"
                value={addQuery}
                onChange={(e) => setAddQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1, ...dialogFieldSx }}
              />

              <List dense sx={dialogListSx}>
                {addCandidates.length === 0 && (
                  <Typography color="text.secondary" sx={{ p: 1.5, textAlign: 'center' }}>
                    No contacts to add.
                  </Typography>
                )}

                {addCandidates.map((u) => {
                  const selected = addTargets.includes(u._id);

                  return (
                    <ListItem key={u._id} disablePadding sx={{ px: 0.5, py: 0.25 }}>
                      <ListItemButton
                        onClick={() =>
                          setAddTargets((prev) =>
                            prev.includes(u._id) ? prev.filter((x) => x !== u._id) : [...prev, u._id]
                          )
                        }
                        selected={selected}
                        sx={{
                          borderRadius: 1,
                          border: '1px solid transparent',
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                          },
                          '&.Mui-selected': {
                            backgroundColor: alpha(accent, 0.14),
                            borderColor: alpha(accent, 0.6),
                          },
                          '&.Mui-selected:hover': { backgroundColor: alpha(accent, 0.2) },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={selected}
                            tabIndex={-1}
                            disableRipple
                            sx={{
                              color: theme.palette.text.secondary,
                              '&.Mui-checked': { color: accent },
                            }}
                          />
                        </ListItemIcon>

                        <UserAvatar selectedUser={u} />

                        <ListItemText
                          sx={{ ml: 1 }}
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                              {u.name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary }}>
                              {u.email || 'No email available'}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>

              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleAddMembers}
                  disabled={addTargets.length === 0 || membersBusy}
                  startIcon={membersBusy ? <CircularProgress size={16} color="inherit" /> : <PersonAdd />}
                  sx={goldButtonSx}
                >
                  Add{addTargets.length > 0 ? ` (${addTargets.length})` : ''}
                </Button>
              </Box>
            </>
          ) : (
            <List dense sx={dialogListSx}>
              {groupMemberUsers.map((u) => {
                const isMe = u._id === auth?._id;
                const isOwnerRow = String(selectedGroup?.createdBy) === String(u._id);

                return (
                  <ListItem
                    key={u._id}
                    disablePadding
                    sx={{ px: 0.5, py: 0.25 }}
                    secondaryAction={
                      iAmOwner && !isMe ? (
                        <Tooltip title="Remove from group">
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              disabled={removingId === u._id}
                              onClick={() => handleRemoveMember(u._id)}
                              aria-label={`Remove ${u.name}`}
                            >
                              {removingId === u._id
                                ? <CircularProgress size={16} />
                                : <Close fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null
                    }
                  >
                    <ListItemButton disableRipple sx={{ borderRadius: 1, cursor: 'default', pr: 6 }}>
                      <UserAvatar selectedUser={u} />

                      <ListItemText
                        sx={{ ml: 1 }}
                        primary={
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                              {isMe ? `${u.name} (You)` : u.name}
                            </Typography>

                            {isOwnerRow && (
                              <Box
                                component="span"
                                sx={{
                                  px: 0.75,
                                  borderRadius: '999px',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  color: accent,
                                  border: `1px solid ${alpha(accent, 0.6)}`,
                                }}
                              >
                                Owner
                              </Box>
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary }}>
                            {u.email || ''}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.alt,
          }}
        >
          <Button
            onClick={() => setMembersOpen(false)}
            disabled={membersBusy}
            sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <RecordingsDialog
        open={recordingsOpen && Boolean(selectedGroup)}
        onClose={() => setRecordingsOpen(false)}
        groupId={selectedGroupId}
        groupName={selectedGroup?.name}
        iAmOwner={iAmOwner}
        myId={String(auth?._id || '')}
      />
      
    </Box>
    </Box>
  )
}

export default Chat